import {
  DATA,
  ABILITY_SLOTS,
  ATTRIBUTE_IDS,
  CLASS_IDS,
  abilityBook,
  type AbilityDef,
  type AbilitySlot,
  type AttributeId,
  type ClassId,
  type CombatFx,
  type EnemyType,
  type EnemyOnHitStatus,
  type EnemyProjectileDef,
  type EnemyGroundAoeDef,
  type EnemyHealDef,
  type BossAbilityDef,
  type BossDef,
  type PhaseDef,
  type HazardKind,
  type PlayerInput,
  type Vec2,
  type DifficultyTier,
  DIFFICULTY_TIERS,
} from '../types.js';
import { EMPTY_INPUT } from '../types.js';
import { effectiveStats } from './attributes.js';
import {
  shopBonuses,
  shopCost,
  shopItem,
  itemBonuses,
  itemDef,
  mergeBonuses,
  recipeDef,
  inventoryContains,
  pickWeighted,
} from './shop.js';
import { classGrowthBonuses, levelForXp } from './progression.js';
import {
  effectiveAbility,
  abilityActivated,
  nodeAbilityId,
  nodeSlot,
  talentNode,
  ownedNodeCount,
} from './talents.js';
import {
  ARENA,
  ENEMY_SPAWNS,
  OBSTACLES,
  PLAYER_SPAWNS,
  resetActiveObstacles,
  setActiveObstacles,
} from '../map/arena.js';
import { resolveAgainstArena, separateBodies } from './collision.js';
import { findPath } from './pathfinding.js';
import { stepLocomotion, angleOf, type Locomotor } from './movement.js';
import { mulberry32 } from './rng.js';
import { waveComposition } from './waves.js';
import {
  applyStatus,
  clearStatuses,
  hasStatus,
  statusMagnitude,
  statusSource,
  tickStatuses,
  tickPeriodicStatuses,
  type StatusEffect,
} from './status.js';
import { TICK_MS, HAZARD_TICK_MS, type GamePhase } from '../config.js';

export interface SimPlayer extends Locomotor {
  id: string;
  name: string;
  cls: ClassId | null;
  locked: boolean;
  connected: boolean;
  /** feature 2.2 — the wavebreak "Ready" vote. Only mutable during `wavebreak`;
   * reset to false on every wave start and on entering a new wavebreak. */
  ready: boolean;
  hp: number;
  maxHp: number;
  mana: number;
  maxMana: number;
  dead: boolean;
  gold: number;
  xp: number;
  /** feature 4.1 — current level, `1..DATA.progression.levelCap`. Re-derived
   * from `xp` via `levelForXp` once per tick (`World.applyLevelUps`); reset to 1
   * on a fresh match (`beginMatch`) and `restartToSelect`. Never decreases. */
  level: number;
  /** feature 4.1 — unspent skill points (1 per level; spent in 4.2 / 4.3). */
  skillPoints: number;
  /** feature 4.1 — unspent free attribute points (spent in 4.4). */
  freeAttrPoints: number;
  /** feature 4.2 — points invested per ability slot. An ability with 0 points
   * cannot be cast (the `resolveCast` activation gate). 1 point activates it;
   * further points (up to `progression.abilityPointCap`) are the budget 4.3's
   * talent nodes consume. All 0 on join; set to 0 on `beginMatch` /
   * `restartToSelect`; refunded to `skillPoints` by `respec`. */
  abilityPoints: { q: number; w: number; e: number; r: number };
  /** feature 4.3 — owned talent node ids, kept sorted (deterministic). Each
   * costs 1 skill point and modifies its parent ability's numbers via
   * `effectiveAbility` once that ability is activated. `[]` on join; reset on
   * `beginMatch` / `restartToSelect`; cleared (and refunded) by `respec`. */
  talentNodes: string[];
  /** feature 4.4 — free attribute points the player has spent, per attribute.
   * Each entry adds +1 to that attribute, folded into the effective stat block
   * by `recomputeStats` exactly like the per-class auto growth. All 0 on join;
   * reset on `beginMatch` / `restartToSelect`; refunded to `freeAttrPoints` (and
   * zeroed) by `respec`. Fixed key order (strength, agility, energy) so
   * `snapshot()` JSON stays deterministic. */
  allocatedAttr: { strength: number; agility: number; energy: number };
  armor: number;
  damage: number;
  attackRange: number;
  attackSpeed: number;
  attackStyle: 'melee' | 'ranged';
  cooldown: number;
  /** Remaining cooldown in ms per ability slot, ordered [q, w, e, r]. */
  abilityCd: number[];
  hpRegenPerSec: number;
  manaRegenPerSec: number;
  strength: number;
  agility: number;
  energy: number;
  /** Value of the class's primary attribute (drives ability scaling). */
  primaryAttrValue: number;
  /** Unmodified class base damage, used by ability damage formulas. */
  baseDamage: number;
  /** Active timed status effects (buffs / debuffs) — see sim/status.ts. */
  statuses: StatusEffect[];
  /** Countdown (ms) to the next Whirlwind damage tick; only meaningful while the
   * `whirlwind` status is active. */
  whirlwindTickTimer: number;
  facing: number;
  moving: boolean;
  lastSeq: number;
  input: PlayerInput;
  spawnIndex: number;
  /** feature 2.3 — per shop catalog item id, how many the player has bought this
   * run. Seeded to 0 for every catalog id (deterministic key order in
   * `snapshot()` regardless of buy order). Persists across waves; reset on a
   * fresh match (`beginMatch`) and `restartToSelect`. */
  shopBuys: Record<string, number>;
  /** feature 3.1 — the 6-slot inventory: an item catalog id or `null` per slot,
   * fixed length 6. Held items sum their `stats` into the effective stat block
   * via `itemBonuses` in `recomputeStats`. All `null` on join; reset on a fresh
   * match (`beginMatch`) and `restartToSelect`. Iterated in slot order
   * (deterministic). */
  inventory: (string | null)[];
  /** feature 2.3 — move speed after shop `moveSpeed` purchases, before any
   * `slow` status. `speed` (the locomotor field) is this scaled down by an
   * active slow on every match tick. */
  baseMoveSpeed: number;
}

interface QueuedCast {
  id: string;
  slot: AbilitySlot;
  x: number;
  y: number;
}

export interface SimEnemy extends Locomotor {
  id: number;
  hp: number;
  maxHp: number;
  damage: number;
  armor: number;
  attackRange: number;
  attackSpeed: number;
  cooldown: number;
  facing: number;
  moving: boolean;
  targetId: string | null;
  repathTimer: number;
  xpReward: number;
  goldReward: number;
  /** Active timed status effects (stun / slow / taunt) — see sim/status.ts. */
  statuses: StatusEffect[];
  /** feature 5.1a — catalog key (mirrored to `EnemySchema.etype`). Set on
   * spawn from the catalog entry; never changes for a live id. */
  etype: string;
  /** feature 5.1a / 5.1b — AI archetype; drives the `tickEnemy` dispatch. */
  ai: 'melee' | 'brute' | 'swarm' | 'kamikaze' | 'ranged' | 'caster';
  /** feature 5.1a — unmodified catalog speed. `speed` (the locomotor field) is
   * this scaled down by an active `slow` on every tick (was the hard-coded
   * `G.speed`). */
  baseSpeed: number;
  /** feature 5.1a — `brute`: px the target hero is shoved on a landed melee
   * hit. 0 for every other archetype. */
  knockback: number;
  /** feature 5.1a — `kamikaze`: explode when a living hero is within this
   * distance. 0 for every other archetype. */
  contactRadius: number;
  /** feature 5.1a — `kamikaze`: explosion damage / radius. 0 for others. */
  explosionDamage: number;
  explosionRadius: number;
  /** feature 5.1b — `ranged` / `caster`: the distance the enemy kites to hold
   * from the nearest hero. 0 for the melee archetypes. */
  preferredRange: number;
  /** feature 5.1b — status applied to the hero on any landed hit (melee /
   * hostile projectile / hostile ground-AoE). Undefined for most types. */
  onHitStatus?: EnemyOnHitStatus;
  /** feature 5.1b — `ranged`: the hostile projectile def. Fired on `cooldown`. */
  projectile?: EnemyProjectileDef;
  /** feature 5.1b — `caster`: the hostile delayed ground-AoE def. Cast on
   * `cooldown`. */
  groundAoe?: EnemyGroundAoeDef;
  /** feature 5.1b — `caster`: the wounded-ally heal def. Fired on `healCooldown`. */
  heal?: EnemyHealDef;
  /** feature 5.1b — `caster`: remaining ms until the next `heal` pulse. Shares
   * the per-tick decrement with `cooldown`. 0 for every other archetype. */
  healCooldown: number;
  /**
   * feature 5.2 — this enemy is a boss (`spawnBoss`). Early-branches `tickEnemy`
   * to `tickBoss`; guards its damage-taken with `invulnUntilTick`; on death
   * clears the wave (see `creditKill`). Absent (not `false`) for every trash mob
   * so a non-boss wave's `snapshot()` JSON is byte-identical to pre-5.2.
   */
  boss?: boolean;
  /** feature 5.2 — `DATA.bosses` key. */
  bossId?: string;
  /** feature 5.2 — index into the boss def's `phases` (0 = opening phase). */
  phaseIndex?: number;
  /** feature 5.2 — index into the current phase's `rotation` (cycled). */
  rotationIndex?: number;
  /** feature 5.2 — the ability currently being cast, or `null` when idle. */
  castAbilityId?: string | null;
  /** feature 5.2 — ms left on the active cast bar. */
  castRemainingMs?: number;
  /** feature 5.2 — full duration of the active cast bar (`castMs`). */
  castTotalMs?: number;
  /** feature 5.2 — ms left on the pacing pause between abilities (`castGapMs`). */
  globalCastGapMs?: number;
  /** feature 5.2 — remaining cooldown (ms) per ability id (fixed key order = the
   * boss def's `abilities` declaration order, for a deterministic snapshot). */
  abilityCooldowns?: Record<string, number>;
  /** feature 5.2 — the boss ignores all incoming damage while
   * `serverTick < invulnUntilTick` (the post-phase-transition window). */
  invulnUntilTick?: number;
  /** feature 5.3a — the boss ignores all incoming damage while
   * `serverTick < shieldUntilTick` (the `selfShield` ability window). Unlike
   * `invulnUntilTick` this does NOT freeze the boss — it keeps moving / casting
   * (§C12). Both windows are honoured by `bossDamageBlocked`. */
  shieldUntilTick?: number;
  /** feature 5.3b — the `BossDef.linkMode: "revive"` link group id (the boss id).
   * Present only on multi-boss `linkMode` bosses; absent (not `''`) for every
   * single boss so their snapshot stays byte-identical to 5.2. */
  linkGroupId?: string;
  /** feature 5.3b — this linked boss is at 0 hp waiting on its revive timer. It
   * stops acting (no cast, no move) but still takes (wasted) damage. */
  downed?: boolean;
  /** feature 5.3b — ms left before a `downed` linked boss revives (if a partner
   * is still up) or, if every partner is also downed, the group dies. */
  reviveTimerMs?: number;
  /** feature 5.3b — the id of the last player to damage this linked boss; the
   * group-death payout credits each twin's gold to its own last hitter. */
  lastHitterId?: string;
}

/**
 * feature 3.4 — an item lying on the arena floor after an enemy drop. Inert
 * until a living player with a free inventory slot walks within
 * `DATA.drops.pickupRadius` of it (see `World.tickGroundItemPickups`). Plain
 * catalog item, no per-instance state.
 */
export interface SimGroundItem {
  id: number;
  itemId: string;
  x: number;
  y: number;
}

/**
 * feature 5.3a — a persistent floor-hazard zone. Every `match` tick (after the
 * periodic-status pass) `tickHazards` ages it (`remainingMs`, deleted at <= 0)
 * and, for every living hero whose centre is within `radius`:
 * - `kind: 'damage'` — `dealEnemyDamage(hero, magnitude)` once per
 *   `HAZARD_TICK_MS` (`tickAccumMs` accumulator, exactly like a periodic status);
 * - `kind: 'slow'` — `applyStatus(hero, 'slow', HAZARD_TICK_MS * 2, magnitude,
 *   sourceId)` every tick (a cheap refresh that lapses ~`HAZARD_TICK_MS` after
 *   the hero leaves the zone).
 * `sourceId` is the spawning enemy id as a string (may be gone). Deterministic:
 * hazards iterate in Map insertion order, heroes in Map order, no `this.rng`.
 */
export interface SimHazard {
  id: number;
  x: number;
  y: number;
  radius: number;
  kind: HazardKind;
  magnitude: number;
  remainingMs: number;
  tickAccumMs: number;
  sourceId: string;
  /** feature 5.3c — `radius` shrinks by `shrinkPerMs * dtMs` each tick, clamped
   * to `minRadius`. Absent → a static-radius hazard (byte-identical to 5.3a/b). */
  shrinkPerMs?: number;
  minRadius?: number;
}

/** feature 2.3 — a fresh purchase-count record: every catalog id at 0, in
 * catalog order. Keeps `SimPlayer.shopBuys` key order stable so `snapshot()`
 * JSON is deterministic no matter which items get bought. */
function emptyShopBuys(): Record<string, number> {
  const b: Record<string, number> = {};
  for (const item of DATA.shop) b[item.id] = 0;
  return b;
}

/** feature 3.1 — a fresh 6-slot inventory, all slots empty. */
function emptyInventory(): (string | null)[] {
  return [null, null, null, null, null, null];
}

/** feature 4.2 — a fresh per-ability point record, every slot un-activated. The
 * key order (q, w, e, r) is fixed so `snapshot()` JSON stays deterministic. */
function emptyAbilityPoints(): SimPlayer['abilityPoints'] {
  return { q: 0, w: 0, e: 0, r: 0 };
}

/** feature 4.4 — a fresh allocated-attribute record, nothing spent. The key
 * order (strength, agility, energy) is fixed so `snapshot()` JSON stays
 * deterministic regardless of allocation order. */
function emptyAllocatedAttr(): SimPlayer['allocatedAttr'] {
  return { strength: 0, agility: 0, energy: 0 };
}

/** feature 3.1 — the inventory length; the fixed slot count (criterion 4). */
const INVENTORY_SLOTS = 6;

/**
 * Flat `ability.id` -> `AbilityDef` lookup across every class book, built once.
 * Ability ids are globally unique. Used to resolve a windup pending effect
 * (feature 1.5) without needing the — possibly disconnected — caster's class.
 */
const ABILITY_BY_ID: Record<string, AbilityDef> = {};
for (const cls of CLASS_IDS) {
  const book = DATA.abilities[cls];
  for (const slot of ABILITY_SLOTS) ABILITY_BY_ID[book[slot].id] = book[slot];
}

/**
 * feature 5.2 (§D13) — the boss id whose `spawnWave` equals `wave`, or `null`.
 * Bosses are iterated in `entities.json` declaration order; a wave has at most
 * one boss (only wave 5 qualifies in 5.2 — waves 10/15/20/25/30 land in 5.3/5.4).
 */
function bossForWave(wave: number): string | null {
  for (const [id, def] of Object.entries(DATA.bosses)) {
    if (id === '_comment') continue; // the entities.json doc key, not a boss
    if (def.spawnWave === wave) return id;
  }
  return null;
}

/**
 * feature 1.5 / 5.1b — a travelling skillshot projectile. Stepped
 * deterministically on the fixed tick (`World.stepProjectiles`).
 *
 * - `owner: 'player'` (feature 1.5) — hits ENEMIES, credits `casterId`, pierces
 *   every enemy it touches. Damage is derived from `abilityId` + the caster's
 *   attributes at hit time.
 * - `owner: 'enemy'` (feature 5.1b) — hits HEROES, no kill credit, does NOT
 *   pierce (expires on the first hit / at range / on a wall). Damage is the flat
 *   `damage` snapshotted here from the enemy's `projectile` def; `casterId` is
 *   `String(enemy.id)`, `abilityId` is `''`.
 */
export interface SimProjectile {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** feature 5.1b — who fired it and, therefore, what it hits. */
  owner: 'player' | 'enemy';
  casterId: string;
  abilityId: string;
  /** Remaining travel distance in px; the projectile expires at `<= 0`. */
  remainingRange: number;
  hitRadius: number;
  /** feature 5.1b — `owner: 'enemy'` only: flat hit damage (armor-mitigated via
   * `dealEnemyDamage`). Unused for player projectiles. */
  damage?: number;
  /** feature 5.1b — `owner: 'enemy'` only: status applied to the hero on hit. */
  onHitStatus?: EnemyOnHitStatus;
  /** Enemy ids already hit — never hit the same enemy twice (player only). */
  pierced: Set<number>;
}

/**
 * feature 1.5 — a scheduled delayed ground-AoE cast by a PLAYER windup ability
 * (Meteor / Entangling Storm / Entangling Roots). Committed at cast time: it
 * resolves at the locked point even if the caster dies or disconnects meanwhile.
 */
interface PlayerPendingEffect {
  source: 'player';
  resolveTick: number;
  casterId: string;
  abilityId: string;
  x: number;
  y: number;
  /** Links to the `telegraphs` entry to drop when this effect resolves. */
  telegraphId: number;
}

/**
 * feature 5.1b — a scheduled delayed ground-AoE cast by a `caster` ENEMY
 * (`goblinShaman`). Also committed on cast: it resolves at the locked point even
 * if the enemy died during the windup (mirrors the 1.5 player rule).
 */
interface EnemyAoePendingEffect {
  source: 'enemy';
  kind: 'aoe';
  resolveTick: number;
  /** `String(enemy.id)` — the enemy may be gone by resolve time. */
  enemyId: string;
  x: number;
  y: number;
  radius: number;
  damage: number;
  onHitStatus?: EnemyOnHitStatus;
  /** feature 5.2 — radial knockback (px) pushing every hit hero away from the
   * blast centre (boss `groundSlam`). 0 / absent for a plain enemy ground-AoE. */
  knockback?: number;
  /** feature 5.3b — when set, `knockback` shoves heroes away from the ARENA
   * CENTRE (toward the lethal edge) instead of the blast centre. Gale Twins
   * `gust`. */
  knockbackFromCentre?: boolean;
  telegraphId: number;
}

/**
 * feature 5.2 — a scheduled boss `dashLine` (Warlord `warlordCharge`). Committed
 * when the cast bar completes: a hostile circle telegraph appears at the locked
 * destination and, `telegraphMs` later, the boss dashes there in a straight line
 * — every hero within `radius` (the corridor half-width) of that line takes
 * `damage` + `knockback` (the Charge corridor scan, reused). Fizzles harmlessly
 * if the boss died during the telegraph.
 */
interface BossDashPendingEffect {
  source: 'enemy';
  kind: 'dash';
  resolveTick: number;
  enemyId: string;
  /** the dash destination (already clamped to the ability's `maxRange`). */
  x: number;
  y: number;
  /** corridor half-width for the en-route hero scan. */
  radius: number;
  damage: number;
  knockback: number;
  onHitStatus?: EnemyOnHitStatus;
  /** feature 5.3b — corridor sweep only; do NOT teleport the boss. Gale Twins
   * `tornadoLine`. */
  noMove?: boolean;
  /** feature 5.3c — drop a line of lingering floor-hazard circles along the dash
   * path on resolve (Furnace Golem `lavaSweep`). */
  lingerHazardKind?: HazardKind;
  lingerHazardMagnitude?: number;
  lingerHazardDurationMs?: number;
  lingerHazardRadius?: number;
  telegraphId: number;
}

/**
 * feature 5.3a — a scheduled boss `hazardSpawn` circle (Bramble Matron
 * `vineEruption`). Committed when the cast bar completes: a hostile circle
 * telegraph appears at the locked point and, `telegraphMs` later, a floor hazard
 * is spawned there via `spawnHazard`. Resolves even if the boss died during the
 * telegraph (mirrors the enemy-AoE rule).
 */
interface HazardPendingEffect {
  source: 'enemy';
  kind: 'hazard';
  resolveTick: number;
  enemyId: string;
  x: number;
  y: number;
  radius: number;
  hazardKind: HazardKind;
  hazardMagnitude: number;
  hazardDurationMs: number;
  telegraphId: number;
}

type EnemyPendingEffect = EnemyAoePendingEffect | BossDashPendingEffect | HazardPendingEffect;

/**
 * feature 1.5 / 5.1b — a scheduled delayed ground-AoE. Player and enemy variants
 * live in the same queue and resolve interleaved in `(resolveTick, insertion)`
 * order (`World.resolvePendingEffects`).
 */
type PendingEffect = PlayerPendingEffect | EnemyPendingEffect;

/** feature 1.5 — a synced danger-zone shown for the whole windup of a pending effect. */
export interface Telegraph {
  id: number;
  x: number;
  y: number;
  radius: number;
  remainingMs: number;
  /** The full windup duration (ms), so the client can draw an accurate fill. */
  totalMs: number;
  /** Client render key: a player ability's `fxKind` / id, or a `caster` enemy's
   * `etype` (feature 5.1b). */
  kind: string;
  /** feature 5.1b — an enemy-sourced danger-zone; the client colours it as a
   * hostile threat, distinct from the player's own windups. */
  hostile: boolean;
}

export class World {
  phase: GamePhase = 'lobby';
  wave = 0;
  /** Seconds remaining for the current select/countdown or wavebreak timer. */
  countdown = 0;
  hostId: string | null = null;
  serverTick = 0;
  /** feature 5.3c / 5.4 — set true when the wave-30 final boss (`onDeath:
   * "winRun"`) dies. The end-of-tick `enemies.size === 0` check in
   * `tickMatch` sees this flag and routes to the terminal `victory` phase
   * instead of `enterWaveBreak()`. Reset on `beginMatch` / `restartToSelect`.
   * Mirrored to `GameState.runWon`. */
  runWon = false;
  /** feature 5.5 — the host-picked difficulty tier, a per-room setting. Settable
   * only via `setDifficulty` (host-only, `phase === 'lobby'` only); NOT reset by
   * `restartToSelect` / `beginMatch` (criterion 4). */
  difficulty: DifficultyTier = 'normal';
  /** feature 5.6 — the run-long, team-shared wave-end revival token budget.
   * `null` = unlimited (today's pre-5.6 behaviour, easy/normal/medium); a
   * number is decremented by 1 in `enterWaveBreak` for every dead player
   * revived at a wave clear, and once it hits 0 a still-dead player stays
   * dead for the rest of the run. Seeded from `DATA.revivalTokens[difficulty]`
   * on `beginMatch` / `restartToSelect` (a per-run resource, unlike
   * `difficulty` itself which is a per-room setting). */
  revivalTokens: number | null = null;

  readonly players = new Map<string, SimPlayer>();
  readonly enemies = new Map<number, SimEnemy>();
  /** feature 1.5 — live skillshot projectiles, keyed by id (spawn order). */
  readonly projectiles = new Map<number, SimProjectile>();
  /** feature 1.5 — active windup danger-zones, keyed by id (one per pending effect). */
  readonly telegraphs = new Map<number, Telegraph>();
  /** feature 3.4 — items dropped by dead enemies, lying on the floor until a
   * player walks over them. Keyed by id in spawn order. */
  readonly groundItems = new Map<number, SimGroundItem>();
  /** feature 5.3a — active floor-hazard zones, keyed by id (spawn order). Cleared
   * with the scheduled effects on wave end / match reset (`clearScheduledEffects`). */
  readonly hazards = new Map<number, SimHazard>();

  private fx: CombatFx[] = [];
  /** Discrete ability casts awaiting deterministic resolution at the top of tickMatch. */
  private castQueue: QueuedCast[] = [];
  /** feature 1.5 — scheduled delayed ground-AoE effects (windup abilities). */
  private pendingEffects: PendingEffect[] = [];
  private nextEnemyId = 1;
  private nextProjectileId = 1;
  private nextTelegraphId = 1;
  /** feature 3.4 — monotonic ground-item id counter (reset on a fresh match). */
  private nextGroundItemId = 1;
  /** feature 5.3a — monotonic hazard id counter (never reset; matches the
   * telegraph / projectile counters). */
  private nextHazardId = 1;
  private rng: () => number;
  private readonly seed: number;

  constructor(seed = (Math.random() * 2 ** 31) | 0) {
    this.seed = seed >>> 0;
    this.rng = mulberry32(this.seed);
  }

  // ---------------------------------------------------------------- membership

  addPlayer(id: string, name: string): void {
    if (this.players.has(id)) {
      const p = this.players.get(id)!;
      p.connected = true;
      p.name = name;
      return;
    }
    const p: SimPlayer = {
      id,
      name,
      cls: null,
      locked: false,
      connected: true,
      ready: false,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      radius: DATA.players.hitboxRadius,
      speed: DATA.players.moveSpeed,
      path: null,
      pathIndex: 0,
      hp: 0,
      maxHp: 0,
      mana: 0,
      maxMana: 0,
      dead: false,
      gold: 0,
      xp: 0,
      level: 1,
      skillPoints: 0,
      freeAttrPoints: 0,
      abilityPoints: emptyAbilityPoints(),
      talentNodes: [],
      allocatedAttr: emptyAllocatedAttr(),
      armor: 0,
      damage: 0,
      attackRange: 0,
      attackSpeed: 1000,
      attackStyle: 'melee',
      cooldown: 0,
      abilityCd: [0, 0, 0, 0],
      hpRegenPerSec: 0,
      manaRegenPerSec: 0,
      strength: 0,
      agility: 0,
      energy: 0,
      primaryAttrValue: 0,
      baseDamage: 0,
      statuses: [],
      whirlwindTickTimer: 0,
      facing: 0,
      moving: false,
      lastSeq: 0,
      input: { ...EMPTY_INPUT },
      spawnIndex: this.players.size % PLAYER_SPAWNS.length,
      shopBuys: emptyShopBuys(),
      inventory: emptyInventory(),
      baseMoveSpeed: DATA.players.moveSpeed,
    };
    this.players.set(id, p);
    if (!this.hostId) this.hostId = id;
  }

  removePlayer(id: string): void {
    this.players.delete(id);
    if (this.hostId === id) {
      this.hostId = this.players.keys().next().value ?? null;
    }
    if (this.phase === 'select' || this.phase === 'countdown') {
      this.reindexSpawns();
      this.evaluateSelectPhase();
    }
  }

  private reindexSpawns(): void {
    let i = 0;
    for (const p of this.players.values()) {
      p.spawnIndex = i % PLAYER_SPAWNS.length;
      i++;
    }
  }

  isHost(id: string): boolean {
    return this.hostId === id;
  }

  /** feature 5.5 (§B5) — the current difficulty tier's flat stat multiplier. */
  private difficultyMultiplier(): number {
    return DATA.difficulty[this.difficulty];
  }

  /**
   * feature 5.5 (§A2/A3) — the host picks a difficulty tier while still in the
   * lobby. Rejected silently unless: `id` is the host, `phase === 'lobby'`, and
   * `tier` is one of the 5 known keys.
   */
  setDifficulty(id: string, tier: string): void {
    if (!this.isHost(id)) return;
    if (this.phase !== 'lobby') return;
    if (!DIFFICULTY_TIERS.includes(tier as DifficultyTier)) return;
    this.difficulty = tier as DifficultyTier;
  }

  // ---------------------------------------------------------------- selection

  /** Host leaves the lobby and opens character selection. */
  beginSelect(): void {
    if (this.phase !== 'lobby' && this.phase !== 'gameover' && this.phase !== 'victory') return;
    for (const p of this.players.values()) {
      p.locked = false;
    }
    this.phase = 'select';
    this.countdown = 0;
    this.reindexSpawns();
  }

  selectClass(id: string, cls: ClassId): void {
    if (this.phase !== 'select' && this.phase !== 'countdown') return;
    const p = this.players.get(id);
    if (!p) return;
    // A class locked in by another player is exclusive.
    for (const other of this.players.values()) {
      if (other.id !== id && other.locked && other.cls === cls) return;
    }
    p.cls = cls;
    p.locked = false;
    this.backToSelect();
  }

  lockIn(id: string): void {
    if (this.phase !== 'select' && this.phase !== 'countdown') return;
    const p = this.players.get(id);
    if (!p || !p.cls) return;
    for (const other of this.players.values()) {
      if (other.id !== id && other.locked && other.cls === p.cls) return;
    }
    p.locked = true;
    this.evaluateSelectPhase();
  }

  unlock(id: string): void {
    const p = this.players.get(id);
    if (!p) return;
    p.locked = false;
    this.backToSelect();
  }

  private backToSelect(): void {
    if (this.phase === 'countdown') {
      this.phase = 'select';
      this.countdown = 0;
    }
  }

  private evaluateSelectPhase(): void {
    if (this.phase !== 'select') return;
    const all = [...this.players.values()];
    if (all.length > 0 && all.every((p) => p.locked && p.cls)) {
      this.phase = 'countdown';
      this.countdown = DATA.waves.selectCountdownSeconds;
    }
  }

  /** Host asks to go back to character select after a game over. */
  restartToSelect(): void {
    this.enemies.clear();
    this.wave = 0;
    this.countdown = 0;
    this.phase = 'select';
    this.fx = [];
    this.castQueue = [];
    this.clearScheduledEffects();
    // feature 5.3c — a full restart clears the run-won flag.
    this.runWon = false;
    // feature 5.2 — never leave a boss's pillar override active across a restart.
    resetActiveObstacles();
    // feature 5.6 — the revival-token budget is a per-run resource; a restart
    // reseeds it from the current tier (`difficulty` itself is untouched by a
    // restart, see the 5.5 persistence tests).
    this.revivalTokens = DATA.revivalTokens[this.difficulty];
    // feature 3.4 — a full restart wipes ground drops (criterion 9).
    this.groundItems.clear();
    this.nextGroundItemId = 1;
    for (const p of this.players.values()) {
      p.locked = false;
      p.ready = false;
      p.dead = false;
      p.hp = 0;
      p.maxHp = 0;
      p.mana = 0;
      p.maxMana = 0;
      p.path = null;
      p.pathIndex = 0;
      p.vx = 0;
      p.vy = 0;
      p.gold = 0;
      p.xp = 0;
      // feature 4.1 — level + accrued level-up points are a per-run resource; a
      // full restart resets them (criteria 5 / 10).
      p.level = 1;
      p.skillPoints = 0;
      p.freeAttrPoints = 0;
      // feature 4.2 — a full restart wipes every activated ability (criterion 1).
      p.abilityPoints = emptyAbilityPoints();
      // feature 4.3 — ...and every owned talent node (criterion 4).
      p.talentNodes = [];
      // feature 4.4 — ...and every spent free attribute point (criterion 1).
      p.allocatedAttr = emptyAllocatedAttr();
      p.cooldown = 0;
      p.abilityCd = [0, 0, 0, 0];
      clearStatuses(p);
      p.whirlwindTickTimer = 0;
      p.input = { ...EMPTY_INPUT };
      // feature 2.3 — shop purchases are a run-long resource; a full restart
      // wipes them (criterion 5).
      p.shopBuys = emptyShopBuys();
      // feature 3.1 — the inventory is a run-long resource; a full restart wipes
      // it (criterion 4).
      p.inventory = emptyInventory();
      p.baseMoveSpeed = DATA.players.moveSpeed;
    }
    this.reindexSpawns();
  }

  // ---------------------------------------------------------------- input

  setInput(id: string, input: PlayerInput): void {
    const p = this.players.get(id);
    if (!p) return;
    p.input = input;
    p.lastSeq = input.seq;
    if (
      this.phase === 'match' &&
      !p.dead &&
      !hasStatus(p, 'stun') &&
      input.clickTick &&
      input.target
    ) {
      const path = findPath({ x: p.x, y: p.y }, input.target);
      p.path = path.length > 0 ? path : null;
      p.pathIndex = 0;
    }
  }

  /**
   * Enqueue a discrete ability cast. Resolved deterministically in arrival order
   * at a fixed point at the top of `tickMatch` (before movement/combat). The
   * cast is validated (and silently rejected) only at resolution time.
   */
  requestCast(id: string, slot: AbilitySlot, x: number, y: number): void {
    if (this.phase !== 'match') return;
    if (!ABILITY_SLOTS.includes(slot)) return;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (this.castQueue.length >= 64) return;
    this.castQueue.push({ id, slot, x, y });
  }

  // ---------------------------------------------------------------- main tick

  tick(dt: number): void {
    this.serverTick++;
    this.fx.length = 0;

    switch (this.phase) {
      case 'countdown':
        this.tickCountdown(dt);
        break;
      case 'match':
        this.tickMatch(dt);
        break;
      case 'wavebreak':
        this.tickWaveBreak(dt);
        break;
      default:
        break;
    }
  }

  private tickCountdown(dt: number): void {
    this.countdown -= dt;
    if (this.countdown <= 0) {
      this.countdown = 0;
      this.beginMatch();
    }
  }

  private beginMatch(): void {
    this.wave = 0;
    this.enemies.clear();
    this.castQueue = [];
    this.clearScheduledEffects();
    // feature 5.3c — a fresh match has not been won.
    this.runWon = false;
    // feature 5.2 — a fresh match always starts on the base arena.
    resetActiveObstacles();
    // feature 5.6 — a fresh match reseeds the revival-token budget from the
    // current tier (see `enterWaveBreak`'s gating below).
    this.revivalTokens = DATA.revivalTokens[this.difficulty];
    // feature 3.4 — ground drops are a run-long thing; a fresh match wipes them
    // (criterion 9). NOT cleared on `enterWaveBreak` — they persist the break.
    this.groundItems.clear();
    this.nextGroundItemId = 1;
    for (const p of this.players.values()) {
      // feature 2.3 — a fresh match starts with an empty shop (criterion 5).
      p.shopBuys = emptyShopBuys();
      // feature 3.1 — ...and an empty inventory (criterion 4).
      p.inventory = emptyInventory();
      // feature 2.1 / 4.1 — a fresh match starts with no gold or XP, at level 1
      // with no accrued level-up points (criterion 5). Reset here (not just in
      // `restartToSelect`) so the invariant holds for any caller — e.g. a future
      // `gameover -> select -> countdown -> beginMatch` path that skips
      // `restartToSelect`. `equipFromClass` -> `recomputeStats` below then sees a
      // zero growth term, so start stats are byte-identical to pre-4.1
      // (criterion 9).
      p.gold = 0;
      p.xp = 0;
      p.level = 1;
      p.skillPoints = 0;
      p.freeAttrPoints = 0;
      // feature 4.2 — zero every activated ability before `equipFromClass` hands
      // out the level-1 skill / attr allotment (criteria 1 / 2).
      p.abilityPoints = emptyAbilityPoints();
      // feature 4.3 — a fresh match starts with no talent nodes (criterion 4).
      p.talentNodes = [];
      // feature 4.4 — ...and no spent free attribute points, so the zero-alloc
      // recomputeStats below is byte-identical to pre-4.4 (criteria 1 / 7).
      p.allocatedAttr = emptyAllocatedAttr();
      this.equipFromClass(p);
      this.resetToSpawn(p, true);
    }
    // feature 2.2 (criterion A3) — open a wavebreak *before* wave 1 so players can
    // shop first. `this.wave` is still 0, so `enterWaveBreak` skips the wave-clear
    // gold bonus; players are already full HP/mana and at spawn.
    this.enterWaveBreak();
  }

  /**
   * Compute a player's effective stats from their class: base class value +
   * attribute contribution (see `effectiveStats`). Also fills mana and clears
   * all cooldowns, so a match / wave 1 opens with a full kit (criterion 22).
   */
  private equipFromClass(p: SimPlayer): void {
    const cls = p.cls ?? 'warrior';
    const base = DATA.classes[cls];
    // feature 2.3 — the attribute-derived fields (+ any shop bonuses in
    // `p.shopBuys`) come from `recomputeStats`; here we only fill the pools and
    // set the fields the shop never touches.
    this.recomputeStats(p);
    // feature 4.2 (criterion 2) — the level-1 grant: a fresh character starts
    // with one skill point + one attr point (4.1 only granted these on level-up
    // >= 2). `beginMatch` zeroed both just above, so this is purely the level-1
    // allotment; `applyLevelUps` adds the per-level grants for levels >= 2.
    p.skillPoints = DATA.progression.skillPointsPerLevel;
    p.freeAttrPoints = DATA.progression.freeAttrPointsPerLevel;
    p.hp = p.maxHp;
    p.mana = p.maxMana;
    p.attackRange = base.attackRange;
    p.attackStyle = base.attackStyle;
    p.radius = base.radius;
    p.cooldown = 0;
    p.abilityCd = [0, 0, 0, 0];
    clearStatuses(p);
    p.whirlwindTickTimer = 0;
    p.dead = false;
  }

  /**
   * feature 2.3 (criterion 10) — (re)derive every shop-affected stat field on
   * `p` from `class base attributes + shop bonuses` (see `effectiveStats` +
   * `shopBonuses`). Shop str/agi/ene buys flow through the attribute formulas;
   * the `maxHp` item adds flat HP; the `moveSpeed` item adds flat move speed.
   * Called from `equipFromClass` and after every successful `buyShopItem`. Does
   * not touch current `hp` / `mana` — the caller decides. With an empty
   * `shopBuys` the assigned values equal the pre-2.3 `equipFromClass` result.
   */
  private recomputeStats(p: SimPlayer): void {
    const cls = p.cls ?? 'warrior';
    // feature 3.1 — merge shop purchases + held items into one `StatBonuses`
    // before the attribute formulas run (criterion 7). With zero shop buys and
    // an empty inventory every field is 0, so the result is byte-identical to
    // the pre-3.1 (and pre-2.3) `effectiveStats(cls)` for all six classes
    // (criterion 8).
    const bonuses = mergeBonuses(
      shopBonuses(p.shopBuys),
      itemBonuses(p.inventory),
      // feature 4.1 (criterion 7) — the per-class auto attribute growth for the
      // player's current level: `classGrowth[cls] * (level - 1)`. All-zero at
      // level 1, so with no shop buys / items the merged result is still
      // byte-identical to the pre-4.1 `effectiveStats(cls)` (criterion 9).
      classGrowthBonuses(cls, p.level),
      // feature 4.4 (criterion 5) — the player's spent free attribute points,
      // folded in exactly like the auto growth. All-zero until the player spends
      // one, so with no shop buys / items / growth the merged result is still
      // byte-identical to the pre-4.4 `effectiveStats(cls)` (criterion 7).
      {
        strength: p.allocatedAttr.strength,
        agility: p.allocatedAttr.agility,
        energy: p.allocatedAttr.energy,
      },
    );
    const s = effectiveStats(cls, bonuses);
    p.maxHp = s.maxHp;
    p.hpRegenPerSec = s.hpRegenPerSec;
    p.armor = s.armor;
    p.damage = s.damage;
    p.baseDamage = s.baseDamage;
    p.attackSpeed = s.attackSpeedMs;
    p.maxMana = s.maxMana;
    p.manaRegenPerSec = s.manaRegenPerSec;
    p.primaryAttrValue = s.primaryAttrValue;
    p.strength = s.strength;
    p.agility = s.agility;
    p.energy = s.energy;
    p.baseMoveSpeed = DATA.players.moveSpeed + bonuses.bonusSpeed;
    p.speed = p.baseMoveSpeed;
  }

  /**
   * feature 4.1 (criterion 6) — settle every classed player's `level` against
   * their current `xp`. Run once per tick AFTER all XP for that tick has been
   * awarded — a dedicated pass near the end of `tickMatch` and (cheap +
   * idempotent, for consistency) `tickWaveBreak`.
   *
   * On a level gain: grant `skillPointsPerLevel` skill points and
   * `freeAttrPointsPerLevel` free attribute points PER level crossed (the
   * `gained` multiplier handles a multi-level jump in one tick), set the new
   * level, then `recomputeStats` — the per-class auto attribute growth
   * (`classGrowthBonuses`) just changed (criteria 7 / 8). Levels never decrease;
   * death does not lose them (XP was withheld while dead — feature 2.1). No RNG;
   * players iterated in Map insertion order.
   */
  private applyLevelUps(): void {
    const prog = DATA.progression;
    for (const p of this.players.values()) {
      if (!p.cls) continue;
      const newLevel = levelForXp(p.xp, p.level);
      if (newLevel <= p.level) continue;
      const gained = newLevel - p.level;
      p.skillPoints += gained * prog.skillPointsPerLevel;
      p.freeAttrPoints += gained * prog.freeAttrPointsPerLevel;
      p.level = newLevel;
      this.recomputeStats(p);
    }
  }

  /**
   * feature 4.2 (criteria 3–6) — invest one skill point into ability `slot`.
   * Silently rejected unless ALL hold: the player exists and has a class; they
   * have at least one unspent skill point; `slot` is below
   * `progression.abilityPointCap`; and — only when this would be the FIRST point
   * in `r` — `level >= progression.ultLevelGate`. On success:
   * `abilityPoints[slot] += 1`, `skillPoints -= 1`. No phase gate — a point can
   * be spent mid-wave (criterion 5). Points past the first do nothing in 4.2
   * (criterion 6). Pure, no RNG.
   */
  spendAbilityPoint(id: string, slot: AbilitySlot): void {
    const p = this.players.get(id);
    if (!p || !p.cls) return;
    if (p.skillPoints < 1) return;
    const prog = DATA.progression;
    if ((p.abilityPoints[slot] ?? 0) >= prog.abilityPointCap) return;
    if (slot === 'r' && p.abilityPoints.r === 0 && p.level < prog.ultLevelGate) return;
    p.abilityPoints[slot] += 1;
    p.skillPoints -= 1;
  }

  /**
   * feature 4.3 (criteria 5–7) — spend one skill point on talent node `nodeId`.
   * Silently rejected unless ALL hold: the player exists and has a class; they
   * have at least one unspent skill point; `nodeId` is a real node; the node's
   * parent ability is activated (`abilityActivated` — a spent point OR a held
   * `+1 <slot>` item, feature 4.5); the node is not already owned; and
   * `abilityPoints[slot] + owned nodes of that ability` is
   * `< progression.abilityPointCap` (the cap counts only SPENT points + owned
   * nodes — a `+1` item is on top of the cap, not consuming it, feature 4.5
   * criterion 8). On success: insert `nodeId` into `talentNodes`
   * (kept sorted), `skillPoints -= 1`. No phase gate — matches
   * `spendAbilityPoint`. Pure, no RNG.
   */
  spendTalentPoint(id: string, nodeId: string): void {
    const p = this.players.get(id);
    if (!p || !p.cls) return;
    if (p.skillPoints < 1) return;
    if (!talentNode(nodeId)) return;
    const slot = nodeSlot(nodeId);
    const abilityId = nodeAbilityId(nodeId);
    if (!slot || !abilityId) return;
    if (!abilityActivated(p, slot)) return; // parent ability not activated (spent point OR +1 item)
    if (p.talentNodes.includes(nodeId)) return; // already owned
    const total = (p.abilityPoints[slot] ?? 0) + ownedNodeCount(p, abilityId);
    if (total >= DATA.progression.abilityPointCap) return;
    p.talentNodes.push(nodeId);
    p.talentNodes.sort();
    p.skillPoints -= 1;
  }

  /**
   * feature 4.4 (criteria 2–4) — spend one free attribute point on `attr`
   * (`'strength'` / `'agility'` / `'energy'`). Silently rejected unless the
   * player exists and has a class and `freeAttrPoints >= 1`. No phase gate —
   * allocation is the player's call any time, matching `spendAbilityPoint` /
   * `spendTalentPoint`. On success: `allocatedAttr[attr] += 1`,
   * `freeAttrPoints -= 1`, `recomputeStats(p)` — the +1 flows through the
   * attribute formulas (maxHp / regen / armor / attackSpeed / damage / mana /
   * `primaryAttrValue`). Pure, no RNG.
   */
  allocateAttr(id: string, attr: AttributeId): void {
    const p = this.players.get(id);
    if (!p || !p.cls) return;
    if (!ATTRIBUTE_IDS.includes(attr)) return;
    if (p.freeAttrPoints < 1) return;
    p.allocatedAttr[attr] += 1;
    p.freeAttrPoints -= 1;
    this.recomputeStats(p);
  }

  /**
   * feature 4.2 (criteria 9–11) / feature 4.3 (criterion 12) / feature 4.4
   * (criterion 8) — refund every invested ability point AND every owned talent
   * node back into `skillPoints`, then zero `abilityPoints` and clear
   * `talentNodes`; also refund every spent free attribute point back into
   * `freeAttrPoints`, zero `allocatedAttr`, and `recomputeStats` (the attribute
   * bonus just went away). Free. Silently rejected unless `phase ===
   * 'wavebreak'` and the player has a class. Pure, no RNG.
   */
  respec(id: string): void {
    if (this.phase !== 'wavebreak') return;
    const p = this.players.get(id);
    if (!p || !p.cls) return;
    const ap = p.abilityPoints;
    p.skillPoints += ap.q + ap.w + ap.e + ap.r + p.talentNodes.length;
    p.abilityPoints = emptyAbilityPoints();
    p.talentNodes = [];
    const alloc = p.allocatedAttr;
    p.freeAttrPoints += alloc.strength + alloc.agility + alloc.energy;
    p.allocatedAttr = emptyAllocatedAttr();
    this.recomputeStats(p);
  }

  /**
   * feature 2.3 (criteria 6–8, 11) — spend the player's own gold on one unit of
   * a shop catalog item. Silently rejected unless ALL hold: `itemId` is a known
   * catalog id; `phase === 'wavebreak'` (the shop is only open in the break);
   * the player exists and has a class; they are below the item's `maxBuys`;
   * their gold covers `shopCost(item, boughtSoFar)`. On success: deduct the
   * cost, bump the per-item count, recompute effective stats, and — a buy always
   * lands in the wavebreak where players sit at full — refill hp + mana so a
   * `+maxHp` buy is usable at once (criterion 11). Deterministic: fixed catalog,
   * no RNG.
   */
  buyShopItem(id: string, itemId: string): void {
    const item = shopItem(itemId);
    if (!item) return;
    if (this.phase !== 'wavebreak') return;
    const p = this.players.get(id);
    if (!p || !p.cls) return;
    const bought = p.shopBuys[itemId] ?? 0;
    if (bought >= item.maxBuys) return;
    const cost = shopCost(item, bought);
    if (p.gold < cost) return;
    p.gold -= cost;
    p.shopBuys[itemId] = bought + 1;
    this.recomputeStats(p);
    p.hp = p.maxHp;
    p.mana = p.maxMana;
  }

  /**
   * feature 3.1 (criteria 9–11) — spend the player's own gold on one item from
   * the fixed catalog. Silently rejected unless ALL hold: `itemId` is a known
   * catalog id; `phase === 'wavebreak'` (the shop is only open in the break);
   * the player exists and has a class; there is a free inventory slot;
   * `gold >= item.cost`. On success: deduct the cost, drop the id into the
   * lowest-index free slot, recompute effective stats, and refill hp + mana (the
   * buy always lands in the wavebreak where players sit at full, so a `+maxHp`
   * item is usable at once). Deterministic: fixed catalog, no RNG.
   */
  buyItem(id: string, itemId: string): void {
    const item = itemDef(itemId);
    if (!item) return;
    if (this.phase !== 'wavebreak') return;
    const p = this.players.get(id);
    if (!p || !p.cls) return;
    const slot = p.inventory.findIndex((s) => s === null);
    if (slot < 0) return; // inventory full (criterion 10)
    if (p.gold < item.cost) return;
    p.gold -= item.cost;
    p.inventory[slot] = itemId;
    this.recomputeStats(p);
    p.hp = p.maxHp;
    p.mana = p.maxMana;
  }

  /**
   * feature 3.1 (criterion 12) — empty inventory `slot` (no refund). Only during
   * `wavebreak`; silently rejected for a bad slot index, an already-empty slot,
   * a missing player / class, or any other phase. Recomputes stats and refills
   * hp + mana afterwards (matches `buyItem`).
   */
  dropItem(id: string, slot: number): void {
    if (this.phase !== 'wavebreak') return;
    const p = this.players.get(id);
    if (!p || !p.cls) return;
    if (!Number.isInteger(slot) || slot < 0 || slot >= INVENTORY_SLOTS) return;
    if (p.inventory[slot] === null) return;
    p.inventory[slot] = null;
    this.recomputeStats(p);
    p.hp = p.maxHp;
    p.mana = p.maxMana;
  }

  /**
   * feature 3.3 (criteria 5–8) — combine held items per a fixed recipe into one
   * bigger item, for free. Silently rejected unless ALL hold: `phase ===
   * 'wavebreak'`; the player exists and has a class; `recipeId` is a known
   * recipe; the inventory currently holds every input id with the required
   * multiplicity (`inventoryContains` — 2× `whetstone` needs two `whetstone`
   * slots). On success: for each input entry null out the lowest-index slot
   * still holding it, drop `output` into the lowest-index free slot (always
   * available — >= 2 inputs consumed, 1 produced), `recomputeStats(p)`, then
   * refill hp + mana (matches `buyItem` / `dropItem`; a forge always lands in
   * the wavebreak where players sit at full). No gold cost, not reversible.
   * Deterministic: fixed recipes, inputs consumed in recipe order against slots
   * in index order, no RNG.
   */
  forge(id: string, recipeId: string): void {
    if (this.phase !== 'wavebreak') return;
    const p = this.players.get(id);
    if (!p || !p.cls) return;
    const recipe = recipeDef(recipeId);
    if (!recipe) return;
    if (!inventoryContains(p.inventory, recipe.inputs)) return;
    for (const inputId of recipe.inputs) {
      const slot = p.inventory.indexOf(inputId);
      p.inventory[slot] = null; // slot >= 0 — inventoryContains guaranteed it
    }
    const free = p.inventory.indexOf(null);
    p.inventory[free] = recipe.output;
    this.recomputeStats(p);
    p.hp = p.maxHp;
    p.mana = p.maxMana;
  }

  private resetToSpawn(p: SimPlayer, resetHp: boolean): void {
    const s = PLAYER_SPAWNS[p.spawnIndex % PLAYER_SPAWNS.length]!;
    p.x = s.x;
    p.y = s.y;
    p.vx = 0;
    p.vy = 0;
    p.path = null;
    p.pathIndex = 0;
    p.input = { ...EMPTY_INPUT };
    if (resetHp) {
      p.hp = p.maxHp;
      p.dead = false;
    }
  }

  /**
   * feature 5.1a — spawn the wave from `waveComposition` instead of the pilot's
   * "N goblins" loop. The per-wave rng is reseeded first (unchanged); the
   * composition consumes its draws, then the spawn loop reuses the existing
   * `ENEMY_SPAWNS` + jitter positioning. For a `ratSwarm` group `count` is the
   * rolled pack size (3–5), so the whole pack lands together. Waves
   * 1..`goblinOnlyThroughWave` are pure goblin (`count === wave`) and the
   * composition makes zero rng draws, so wave 1 is byte-identical to the pilot.
   */
  private startNextWave(): void {
    // feature 2.2 (criterion 8) — a fresh wave always starts with nobody ready.
    for (const p of this.players.values()) p.ready = false;
    this.wave++;
    this.rng = mulberry32(this.seed ^ (this.wave * 0x9e3779b1));
    // feature 5.2 (§D13) — a boss wave spawns ONLY that boss (no
    // `waveComposition`, no `this.rng` draws beyond `spawnBoss`'s none). Non-boss
    // waves are byte-identical to pre-5.2 (§G19).
    const bossId = bossForWave(this.wave);
    if (bossId) {
      this.spawnBoss(bossId);
      return;
    }
    const jitter = 24;
    for (const group of waveComposition(this.wave, this.rng)) {
      for (let i = 0; i < group.count; i++) {
        const spawn = ENEMY_SPAWNS[Math.floor(this.rng() * ENEMY_SPAWNS.length)]!;
        this.spawnEnemy(
          group.etype,
          spawn.x + (this.rng() - 0.5) * jitter,
          spawn.y + (this.rng() - 0.5) * jitter,
        );
      }
    }
  }

  /**
   * feature 5.1a — build a `SimEnemy` from the catalog entry for `etype`
   * (replaces the pilot's hard-coded `spawnGoblin`). Id allocation is unchanged
   * (`nextEnemyId++`). The `repathTimer` seed draw keeps its position in the
   * rng sequence so a goblin-only wave is byte-identical to the pilot.
   */
  private spawnEnemy(etype: EnemyType, x: number, y: number): void {
    const c = DATA.enemies[etype];
    // feature 5.5 (§B5) — hp/damage/armor scale by the room's difficulty
    // multiplier, applied once here (not per-tick). Rewards, speed, radius and
    // onHitStatus magnitudes are untouched (criterion 7). At `normal` (×1.0)
    // this is a no-op, so pre-5.5 output stays byte-identical (criterion 8).
    const mul = this.difficultyMultiplier();
    const hp = c.hp * mul;
    // feature 5.5 — a non-boss enemy's REAL outgoing damage isn't always the
    // top-level `damage` field: `kamikaze` deals `explosionDamage`, `ranged`
    // deals `projectile.damage`, `caster` deals `groundAoe.damage`. All three
    // scale the same way, at spawn. `projectile` / `groundAoe` are shallow-
    // copied (never mutated in place) — the catalog object is a shared
    // reference across every spawn of this `etype`, so mutating it would
    // compound the multiplier on every later spawn (and leak across rooms /
    // tests). At `normal` (×1.0) the copy is still a no-op value-wise.
    const projectile = c.projectile
      ? { ...c.projectile, damage: c.projectile.damage * mul }
      : undefined;
    const groundAoe = c.groundAoe
      ? { ...c.groundAoe, damage: c.groundAoe.damage * mul }
      : undefined;
    const e: SimEnemy = {
      id: this.nextEnemyId++,
      x,
      y,
      vx: 0,
      vy: 0,
      radius: c.radius,
      speed: c.speed,
      path: null,
      pathIndex: 0,
      hp,
      maxHp: hp,
      damage: c.damage * mul,
      armor: c.armor * mul,
      attackRange: c.attackRange,
      attackSpeed: c.attackSpeed,
      cooldown: 0,
      facing: 0,
      moving: false,
      targetId: null,
      repathTimer: this.rng() * 0.5,
      xpReward: c.xpReward,
      goldReward: c.goldReward,
      statuses: [],
      etype,
      ai: c.ai,
      baseSpeed: c.speed,
      knockback: c.knockback ?? 0,
      contactRadius: c.contactRadius ?? 0,
      explosionDamage: (c.explosionDamage ?? 0) * mul,
      explosionRadius: c.explosionRadius ?? 0,
      preferredRange: c.preferredRange ?? 0,
      onHitStatus: c.onHitStatus,
      projectile,
      groundAoe,
      heal: c.heal,
      healCooldown: 0,
    };
    this.enemies.set(e.id, e);
  }

  // ------------------------------------------------------------ feature 5.2 boss

  /**
   * feature 5.2 (§C) / 5.3b (§A) — build the boss as `spawnCount` (default 1)
   * `SimEnemy` entities with `boss: true` (so the existing death / targeting /
   * separation / snapshot code all just works) plus the boss runtime fields,
   * drop them into `this.enemies`, and — if the boss def carries `pillars` (§D14)
   * — swap the active arena obstacle set to `base + pillars` for the fight
   * (reverted by `enterWaveBreak`). A single boss spawns at the top-centre of the
   * arena, identical to 5.2; a multi-boss group is spread on fixed x offsets
   * around that point (no `this.rng` draw here — § determinism). The
   * `activeObstacles` swap, `floorHazard` and `edgeHazard` (§C11) are applied
   * ONCE for the group, not per entity.
   */
  private spawnBoss(bossId: string): void {
    const b = DATA.bosses[bossId];
    if (!b) return;
    const spawnCount = Math.max(1, b.spawnCount ?? 1);
    const linked = b.linkMode === 'revive' && spawnCount > 1;
    let firstId = 0;
    // feature 5.5 (§B6) — hp/armor scale by the room's difficulty multiplier,
    // applied once at spawn (a no-op at `normal`, criterion 8). Boss ability
    // damage is scaled separately, at resolve time, via `bossAbilityDamage`.
    const mul = this.difficultyMultiplier();
    const bossHp = b.hp * mul;
    const bossArmor = b.armor * mul;
    for (let i = 0; i < spawnCount; i++) {
      // fixed offsets: a lone boss keeps the exact 5.2 spawn point (0 offset), a
      // group is centred and spread 160 px apart along x.
      const offsetX = spawnCount === 1 ? 0 : (i - (spawnCount - 1) / 2) * 160;
      const cooldowns: Record<string, number> = {};
      for (const key of Object.keys(b.abilities)) cooldowns[key] = 0;
      const e: SimEnemy = {
        id: this.nextEnemyId++,
        x: ARENA.width / 2 + offsetX,
        y: 250,
        vx: 0,
        vy: 0,
        radius: b.radius,
        speed: b.moveSpeed,
        path: null,
        pathIndex: 0,
        hp: bossHp,
        maxHp: bossHp,
        damage: 0,
        armor: bossArmor,
        attackRange: 0,
        attackSpeed: 1000,
        cooldown: 0,
        facing: Math.PI / 2,
        moving: false,
        targetId: null,
        repathTimer: 0,
        xpReward: b.xpReward,
        goldReward: b.goldReward,
        statuses: [],
        etype: bossId,
        ai: 'melee',
        baseSpeed: b.moveSpeed,
        knockback: 0,
        contactRadius: 0,
        explosionDamage: 0,
        explosionRadius: 0,
        preferredRange: 0,
        healCooldown: 0,
        boss: true,
        bossId,
        phaseIndex: 0,
        rotationIndex: 0,
        castAbilityId: null,
        castRemainingMs: 0,
        castTotalMs: 0,
        globalCastGapMs: b.phases[0]!.castGapMs * (b.phases[0]!.enrage?.castGapMul ?? 1),
        abilityCooldowns: cooldowns,
        invulnUntilTick: 0,
        shieldUntilTick: 0,
      };
      // feature 5.3b — only a real link group carries these fields, so a single
      // boss's snapshot JSON is byte-identical to 5.2.
      if (linked) {
        e.linkGroupId = bossId;
        e.downed = false;
        e.reviveTimerMs = 0;
      }
      if (i === 0) firstId = e.id;
      this.enemies.set(e.id, e);
    }
    if (b.pillars && b.pillars.length > 0) {
      setActiveObstacles(OBSTACLES.concat(b.pillars));
    }
    // feature 5.3a (§C11) — the Frost Revenant's "icy floor": one big `slow`
    // hazard at the arena centre, lifetime long enough to outlast the fight.
    // Cleared on wave end with the other scheduled effects.
    if (b.floorHazard) {
      const fh = b.floorHazard;
      this.spawnHazard(
        ARENA.width / 2,
        ARENA.height / 2,
        fh.radius,
        fh.kind,
        fh.magnitude,
        fh.durationMs,
        String(firstId),
      );
    }
    // feature 5.3b (§C11) — the Gale Twins' lethal edge: a ring of overlapping
    // large `damage` hazard circles walked clockwise around the arena perimeter,
    // `inset` px inside the walls, lasting the whole fight.
    if (b.edgeHazard) this.spawnEdgeHazards(b.edgeHazard, firstId);
    // feature 5.3c (§B7) — bespoke arena hazards (the Furnace Golem's shrinking
    // `burnOutside` safe zone + fixed `safe` cooling vents). Each is placed at
    // `(arenaCentre + offset)` and lasts the whole fight.
    for (const ah of b.arenaHazards ?? []) {
      this.spawnHazard(
        ARENA.width / 2 + (ah.offsetX ?? 0),
        ARENA.height / 2 + (ah.offsetY ?? 0),
        ah.radius,
        ah.kind,
        ah.magnitude,
        ah.durationMs,
        String(firstId),
        ah.shrinkPerMs,
        ah.minRadius,
      );
    }
    // feature 5.3c (§E13) — adds on the OPENING phase (later phases spawn theirs
    // on the phase transition in `tickBoss`).
    const first = this.enemies.get(firstId);
    if (first && b.phases[0]?.summonAdds) this.spawnAdds(b.phases[0]!.summonAdds!, first);
  }

  /**
   * feature 5.2 / 5.3c — spawn each `spawns` entry's `count` of `etype` in a ring
   * around `e`. The only `this.rng` use in the boss script — a fixed number of
   * draws in a fixed order (angle then radius per add, then `spawnEnemy`'s own
   * `repathTimer` seed draw). Shared by `summon` abilities and phase-entry adds.
   */
  private spawnAdds(spawns: { etype: EnemyType; count: number }[], e: SimEnemy): void {
    for (const spec of spawns) {
      for (let i = 0; i < spec.count; i++) {
        const ang = this.rng() * Math.PI * 2;
        const rad = e.radius + 24 + this.rng() * 44;
        this.spawnEnemy(spec.etype, e.x + Math.cos(ang) * rad, e.y + Math.sin(ang) * rad);
      }
    }
  }

  /**
   * feature 5.3b (§C11) — place `eh.count` `damage` hazard circles evenly along
   * the arena perimeter, `eh.inset` px inside the walls, walking clockwise from
   * the top-left corner. Deterministic: a fixed formula, no `this.rng`.
   */
  private spawnEdgeHazards(eh: NonNullable<BossDef['edgeHazard']>, sourceId: number): void {
    const x0 = eh.inset;
    const y0 = eh.inset;
    const x1 = ARENA.width - eh.inset;
    const y1 = ARENA.height - eh.inset;
    const spanX = x1 - x0;
    const spanY = y1 - y0;
    const perimeter = 2 * (spanX + spanY);
    for (let i = 0; i < eh.count; i++) {
      const d = (i / eh.count) * perimeter;
      let px: number;
      let py: number;
      if (d < spanX) {
        px = x0 + d;
        py = y0;
      } else if (d < spanX + spanY) {
        px = x1;
        py = y0 + (d - spanX);
      } else if (d < 2 * spanX + spanY) {
        px = x1 - (d - spanX - spanY);
        py = y1;
      } else {
        px = x0;
        py = y1 - (d - 2 * spanX - spanY);
      }
      this.spawnHazard(px, py, eh.radius, 'damage', eh.magnitude, eh.durationMs, String(sourceId));
    }
  }

  /**
   * feature 5.2 (§C10) — one boss tick. In order:
   *  1. decrement the per-ability cooldowns + the global cast-gap timer, and (if
   *     not stunned) the active cast bar;
   *  2. phase check — when `hp/maxHp <= phases[phaseIndex+1].hpFrac`, advance the
   *     phase: set the ~1 s invuln window, reset `rotationIndex`, adopt the new
   *     phase's `castGapMs`, drop any in-progress cast, emit a phase `fx`, and
   *     settle this tick;
   *  3. while stunned OR invulnerable — freeze (no cast, no move);
   *  4. if casting and the bar elapsed — resolve the ability, start its cooldown,
   *     advance `rotationIndex`, start the global gap;
   *  5. else if idle and the global gap elapsed — pick the next rotation ability
   *     off cooldown (a `melee` needs a hero in reach); start its cast bar, or
   *     resolve it immediately when it has no `castMs`;
   *  6. otherwise walk toward the nearest living hero (melee-chase locomotion).
   *
   * Deterministic: fixed cooldowns + the global gap, nearest / farthest-hero
   * picks broken by Map insertion order, `this.rng` only in `summon` jitter.
   */
  private tickBoss(e: SimEnemy, dt: number, dtMs: number): void {
    const b = DATA.bosses[e.bossId ?? ''];
    if (!b) return;
    // feature 5.3b (§B6) — a downed linked twin does nothing; it just waits on
    // its revive timer (`tickLinkedBosses`), still taking (wasted) damage.
    if (e.downed) {
      e.vx = 0;
      e.vy = 0;
      e.moving = false;
      e.path = null;
      return;
    }
    const stunned = hasStatus(e, 'stun');
    const phase = b.phases[e.phaseIndex ?? 0]!;

    // 1. timers
    const cds = e.abilityCooldowns ?? {};
    for (const key of Object.keys(cds)) {
      if (cds[key]! > 0) cds[key] = Math.max(0, cds[key]! - dtMs);
    }
    if ((e.globalCastGapMs ?? 0) > 0) {
      e.globalCastGapMs = Math.max(0, (e.globalCastGapMs ?? 0) - dtMs);
    }
    if (e.castAbilityId != null && !stunned) {
      e.castRemainingMs = Math.max(0, (e.castRemainingMs ?? 0) - dtMs);
    }

    // 2. phase transition
    const next = b.phases[(e.phaseIndex ?? 0) + 1];
    if (next && e.hp / e.maxHp <= next.hpFrac) {
      e.phaseIndex = (e.phaseIndex ?? 0) + 1;
      e.rotationIndex = 0;
      e.invulnUntilTick = this.serverTick + Math.ceil(b.invulnMs / TICK_MS);
      const entered = b.phases[e.phaseIndex]!;
      e.globalCastGapMs = this.bossCastGap(entered);
      e.castAbilityId = null;
      e.castRemainingMs = 0;
      e.castTotalMs = 0;
      e.vx = 0;
      e.vy = 0;
      e.moving = false;
      e.path = null;
      this.fx.push({ kind: 'enemyExplode', x: e.x, y: e.y, radius: b.radius * 3 });
      // feature 5.3c (§E13) — "adds every 20%": spawn this phase's adds on entry.
      if (entered.summonAdds) this.spawnAdds(entered.summonAdds, e);
      return;
    }

    const invuln = this.serverTick < (e.invulnUntilTick ?? 0);
    if (stunned || invuln) {
      e.vx = 0;
      e.vy = 0;
      e.moving = false;
      e.path = null;
      return;
    }

    // nearest living hero (target + melee-chase); no RNG, Map-order tiebreak.
    let target: SimPlayer | null = null;
    let best = Infinity;
    for (const p of this.players.values()) {
      if (!World.isLiving(p)) continue;
      const d = (p.x - e.x) ** 2 + (p.y - e.y) ** 2;
      if (d < best) {
        best = d;
        target = p;
      }
    }
    e.targetId = target?.id ?? null;

    // 4. resolve an in-progress cast
    if (e.castAbilityId != null) {
      if ((e.castRemainingMs ?? 0) <= 0) {
        const abilityId = e.castAbilityId;
        const ab = b.abilities[abilityId]!;
        this.resolveBossAbility(e, ab, target);
        cds[abilityId] = ab.cooldownMs;
        e.castAbilityId = null;
        e.castRemainingMs = 0;
        e.castTotalMs = 0;
        e.rotationIndex = ((e.rotationIndex ?? 0) + 1) % phase.rotation.length;
        e.globalCastGapMs = this.bossCastGap(phase);
      }
      e.vx = 0;
      e.vy = 0;
      e.moving = false;
      e.path = null;
      if (target) e.facing = Math.atan2(target.y - e.y, target.x - e.x);
      return;
    }

    // 5. start the next ability once the global gap has elapsed
    if ((e.globalCastGapMs ?? 0) <= 0 && target) {
      const rot = phase.rotation;
      for (let k = 0; k < rot.length; k++) {
        const idx = ((e.rotationIndex ?? 0) + k) % rot.length;
        const abilityId = rot[idx]!;
        const ab = b.abilities[abilityId];
        if (!ab) continue;
        if ((cds[abilityId] ?? 0) > 0) continue;
        if (ab.kind === 'melee') {
          const reach = (ab.radius ?? 0) + e.radius + target.radius;
          if (Math.hypot(target.x - e.x, target.y - e.y) > reach) continue;
        }
        e.rotationIndex = idx;
        e.facing = Math.atan2(target.y - e.y, target.x - e.x);
        if (ab.castMs && ab.castMs > 0) {
          e.castAbilityId = abilityId;
          e.castRemainingMs = ab.castMs;
          e.castTotalMs = ab.castMs;
          e.vx = 0;
          e.vy = 0;
          e.moving = false;
          e.path = null;
          return;
        }
        // instant ability (a `melee` fallback with no cast bar)
        this.resolveBossAbility(e, ab, target);
        cds[abilityId] = ab.cooldownMs;
        e.rotationIndex = ((e.rotationIndex ?? 0) + 1) % rot.length;
        e.globalCastGapMs = this.bossCastGap(phase);
        return;
      }
    }

    // 6. melee-chase toward the nearest living hero
    if (!target) {
      e.vx = 0;
      e.vy = 0;
      e.moving = false;
      return;
    }
    const dist = Math.hypot(target.x - e.x, target.y - e.y);
    if (dist - e.radius - target.radius <= 8) {
      e.vx = 0;
      e.vy = 0;
      e.moving = false;
      e.path = null;
      e.facing = Math.atan2(target.y - e.y, target.x - e.x);
      return;
    }
    e.repathTimer -= dt;
    if (e.repathTimer <= 0 || !e.path) {
      e.repathTimer = 0.4;
      const path = findPath({ x: e.x, y: e.y }, { x: target.x, y: target.y });
      e.path = path.length > 0 ? path : null;
      e.pathIndex = 0;
    }
    const slow = statusMagnitude(e, 'slow');
    // feature 5.3c (§D11) — an `enrage` phase also speeds the boss up.
    e.speed = e.baseSpeed * Math.max(0, 1 - slow) * this.bossEnrage(e).speedMul;
    e.moving = stepLocomotion(e, 0, 0, dt);
    e.facing = angleOf(e.vx, e.vy, e.facing);
  }

  /**
   * feature 5.3c (§D11) — the current phase's `enrage` multipliers (each defaults
   * to 1). Deterministic — a pure read off `DATA.bosses`.
   */
  private bossEnrage(e: SimEnemy): { damageMul: number; speedMul: number; castGapMul: number } {
    const en = DATA.bosses[e.bossId ?? '']?.phases[e.phaseIndex ?? 0]?.enrage;
    return {
      damageMul: en?.damageMul ?? 1,
      speedMul: en?.speedMul ?? 1,
      castGapMul: en?.castGapMul ?? 1,
    };
  }

  /** feature 5.3c / 5.5 (§B6) — an ability's outgoing damage, scaled by the
   * current phase's `enrage.damageMul` composed with the room's difficulty
   * multiplier (both default to 1 → byte-identical at pre-5.3c/pre-5.5 baseline). */
  private bossAbilityDamage(e: SimEnemy, ab: BossAbilityDef): number {
    return (ab.damage ?? 0) * this.bossEnrage(e).damageMul * this.difficultyMultiplier();
  }

  /** feature 5.3c — a phase's pacing gap, scaled by its `enrage.castGapMul`. */
  private bossCastGap(phase: PhaseDef): number {
    return phase.castGapMs * (phase.enrage?.castGapMul ?? 1);
  }

  /** feature 5.3c (§A2) — is `p`'s centre inside any `safe` hazard right now? */
  private heroInAnySafeHazard(p: SimPlayer): boolean {
    for (const h of this.hazards.values()) {
      if (h.kind !== 'safe') continue;
      if (Math.hypot(p.x - h.x, p.y - h.y) <= h.radius) return true;
    }
    return false;
  }

  /** feature 5.2 (§C11) / 5.3a (§D15) — a boss ignores all incoming damage while
   * `serverTick < invulnUntilTick` (the post-phase-transition window) OR
   * `serverTick < shieldUntilTick` (an active `selfShield`). */
  private bossDamageBlocked(e: SimEnemy): boolean {
    if (!e.boss) return false;
    return this.serverTick < (e.invulnUntilTick ?? 0) || this.serverTick < (e.shieldUntilTick ?? 0);
  }

  /** feature 5.2 (§C10) — dispatch a resolved boss ability by `kind`. */
  private resolveBossAbility(e: SimEnemy, ab: BossAbilityDef, target: SimPlayer | null): void {
    switch (ab.kind) {
      case 'groundAoe':
        this.bossGroundAoe(e, ab);
        break;
      case 'summon':
        this.bossSummon(e, ab);
        break;
      case 'dashLine':
        this.bossDashLine(e, ab);
        break;
      case 'projectileVolley':
        this.bossVolley(e, ab, target);
        break;
      case 'melee':
        this.bossMelee(e, ab, target);
        break;
      case 'hazardSpawn':
        this.bossHazardSpawn(e, ab);
        break;
      case 'selfShield':
        this.bossSelfShield(e, ab);
        break;
      case 'arenaDamage':
        this.bossArenaDamage(e, ab);
        break;
    }
  }

  /**
   * feature 5.3c (§C10) — `arenaDamage` (Furnace Golem `overheatPulse`, Arena
   * Master `furnacePulse`): on resolve, `dealEnemyDamage` (enrage-scaled) to every
   * living hero NOT standing in a hazard of `ab.exemptHazardKind` (`'safe'` — a
   * cooling vent). Arena-wide, no telegraph — the cast bar was the warning.
   * Heroes iterated in Map insertion order; no RNG.
   */
  private bossArenaDamage(e: SimEnemy, ab: BossAbilityDef): void {
    const dmg = this.bossAbilityDamage(e, ab);
    this.fx.push({ kind: 'enemyExplode', x: e.x, y: e.y, radius: e.radius * 6 });
    for (const p of this.players.values()) {
      if (!World.isLiving(p)) continue;
      if (ab.exemptHazardKind === 'safe' && this.heroInAnySafeHazard(p)) continue;
      this.dealEnemyDamage(p, dmg);
      this.fx.push({ kind: 'hit', x: p.x, y: p.y });
    }
  }

  /**
   * feature 5.2 — `groundAoe` (Warlord `groundSlam`): schedule a hostile
   * danger-zone at the boss's current position that resolves `windupMs` later
   * (the 5.1b `schedulePendingEnemyAoe` machinery), carrying the ability's
   * radial `knockback` so `resolveEnemyAoe` shoves survivors outward.
   */
  private bossGroundAoe(e: SimEnemy, ab: BossAbilityDef): void {
    const windupMs = ab.windupMs ?? 0;
    const baseRadius = ab.radius ?? 0;
    // feature 5.3a — `stagger` (Frost Revenant `frostNova`): fire N boss-centred
    // circles, each `staggerRadiusStep` px larger and `staggerDelayMs` later, so
    // there is a moment to move between the expanding fronts. `stagger` omitted /
    // 1 reproduces the plain single-circle 5.2 behaviour exactly (§18).
    const stagger = Math.max(1, ab.stagger ?? 1);
    const step = ab.staggerRadiusStep ?? 0;
    const delay = ab.staggerDelayMs ?? 0;
    for (let i = 0; i < stagger; i++) {
      const radius = baseRadius + step * i;
      const extraDelay = delay * i;
      const telegraphId = this.nextTelegraphId++;
      this.pendingEffects.push({
        source: 'enemy',
        kind: 'aoe',
        resolveTick: this.serverTick + Math.ceil((windupMs + extraDelay) / TICK_MS),
        enemyId: String(e.id),
        x: e.x,
        y: e.y,
        radius,
        damage: this.bossAbilityDamage(e, ab),
        onHitStatus: ab.onHitStatus,
        knockback: ab.knockback ?? 0,
        knockbackFromCentre: ab.knockbackFromArenaCentre,
        telegraphId,
      });
      this.telegraphs.set(telegraphId, {
        id: telegraphId,
        x: e.x,
        y: e.y,
        radius,
        remainingMs: windupMs + extraDelay,
        totalMs: windupMs + extraDelay,
        kind: e.bossId ?? 'boss',
        hostile: true,
      });
    }
  }

  /**
   * feature 5.3a (§B9 / §D14) — `hazardSpawn` (Bramble Matron `vineEruption`):
   * telegraph `count` hostile circles — one at each living hero's position (Map
   * order), then any remainder in a ring around the boss — that each become a
   * floor hazard `telegraphMs` later (`resolveHazardSpawn` → `spawnHazard`).
   * Deterministic: heroes in Map insertion order, ring fill is a fixed formula,
   * no `this.rng`.
   */
  private bossHazardSpawn(e: SimEnemy, ab: BossAbilityDef): void {
    const count = Math.max(1, ab.count ?? 1);
    const radius = ab.radius ?? 0;
    const telegraphMs = ab.telegraphMs ?? 0;
    const kind = ab.hazardKind ?? 'damage';
    const magnitude = ab.hazardMagnitude ?? 0;
    const durationMs = ab.hazardDurationMs ?? 0;

    const pts: { x: number; y: number }[] = [];
    for (const p of this.players.values()) {
      if (!World.isLiving(p)) continue;
      if (pts.length >= count) break;
      pts.push({ x: p.x, y: p.y });
    }
    for (let i = pts.length; i < count; i++) {
      const ang = (i / count) * Math.PI * 2;
      const rad = e.radius + 90;
      pts.push({ x: e.x + Math.cos(ang) * rad, y: e.y + Math.sin(ang) * rad });
    }

    for (const pt of pts) {
      const telegraphId = this.nextTelegraphId++;
      this.pendingEffects.push({
        source: 'enemy',
        kind: 'hazard',
        resolveTick: this.serverTick + Math.ceil(telegraphMs / TICK_MS),
        enemyId: String(e.id),
        x: pt.x,
        y: pt.y,
        radius,
        hazardKind: kind,
        hazardMagnitude: magnitude,
        hazardDurationMs: durationMs,
        telegraphId,
      });
      this.telegraphs.set(telegraphId, {
        id: telegraphId,
        x: pt.x,
        y: pt.y,
        radius,
        remainingMs: telegraphMs,
        totalMs: telegraphMs,
        kind: e.bossId ?? 'boss',
        hostile: true,
      });
    }
  }

  /**
   * feature 5.3a (§C12 / §D15) — `selfShield` (Frost Revenant `hoarfrostGuard`):
   * the boss ignores all damage for `durationMs` (a separate window from the
   * phase-transition `invulnUntilTick`, so it does NOT freeze the boss — it keeps
   * moving and casting). Does not reset the rotation or count as a phase change.
   */
  private bossSelfShield(e: SimEnemy, ab: BossAbilityDef): void {
    const durationMs = ab.durationMs ?? 0;
    e.shieldUntilTick = this.serverTick + Math.ceil(durationMs / TICK_MS);
    this.fx.push({ kind: 'enemyHeal', x: e.x, y: e.y });
  }

  /**
   * feature 5.2 — `summon` (Warlord `summonBrutes`): spawn each `spawn` entry's
   * `count` of `etype` in a ring around the boss. The ONLY `this.rng` use in the
   * boss script — a fixed number of draws in a fixed order (angle then radius per
   * add, then `spawnEnemy`'s own `repathTimer` seed draw).
   */
  private bossSummon(e: SimEnemy, ab: BossAbilityDef): void {
    this.spawnAdds(ab.spawn ?? [], e);
    this.fx.push({ kind: 'enemyExplode', x: e.x, y: e.y, radius: e.radius * 2 });
  }

  /**
   * feature 5.2 — `dashLine` (Warlord `warlordCharge`): lock the FARTHEST living
   * hero's position (clamped to `maxRange`), schedule a hostile circle telegraph
   * there, and — `telegraphMs` later — dash the boss to it, corridor-scanning
   * heroes en route (`resolveBossDash`). The telegraph is a circle at the
   * destination, not a true line — see deferred-questions 5.2.
   */
  private bossDashLine(e: SimEnemy, ab: BossAbilityDef): void {
    let destX = e.x;
    let destY = e.y;
    let far = -1;
    for (const p of this.players.values()) {
      if (!World.isLiving(p)) continue;
      const d = (p.x - e.x) ** 2 + (p.y - e.y) ** 2;
      if (d > far) {
        far = d;
        destX = p.x;
        destY = p.y;
      }
    }
    let dx = destX - e.x;
    let dy = destY - e.y;
    const dist = Math.hypot(dx, dy);
    const max = ab.maxRange ?? 0;
    if (dist > max && dist > 1e-6) {
      dx = (dx / dist) * max;
      dy = (dy / dist) * max;
    }
    const tx = e.x + dx;
    const ty = e.y + dy;
    const half = (ab.corridorWidth ?? 0) / 2;
    const windupMs = ab.telegraphMs ?? 0;
    const telegraphId = this.nextTelegraphId++;
    this.pendingEffects.push({
      source: 'enemy',
      kind: 'dash',
      resolveTick: this.serverTick + Math.ceil(windupMs / TICK_MS),
      enemyId: String(e.id),
      x: tx,
      y: ty,
      radius: half,
      damage: this.bossAbilityDamage(e, ab),
      knockback: ab.knockback ?? 0,
      onHitStatus: ab.onHitStatus,
      noMove: ab.noMove,
      lingerHazardKind: ab.lingerHazardKind,
      lingerHazardMagnitude: ab.lingerHazardMagnitude,
      lingerHazardDurationMs: ab.lingerHazardDurationMs,
      lingerHazardRadius: ab.lingerHazardRadius,
      telegraphId,
    });
    this.telegraphs.set(telegraphId, {
      id: telegraphId,
      x: tx,
      y: ty,
      radius: half,
      remainingMs: windupMs,
      totalMs: windupMs,
      kind: e.bossId ?? 'boss',
      hostile: true,
    });
  }

  /**
   * feature 5.2 — `projectileVolley` (framework only; the Warlord has none):
   * fire `projectiles` hostile shots in a `spreadDeg` fan toward the nearest
   * hero, reusing `spawnEnemyProjectile` with an explicit def built from the
   * ability numbers.
   */
  private bossVolley(e: SimEnemy, ab: BossAbilityDef, target: SimPlayer | null): void {
    const n = Math.max(1, ab.projectiles ?? 1);
    const baseAng = target ? Math.atan2(target.y - e.y, target.x - e.x) : e.facing;
    const spread = ((ab.spreadDeg ?? 0) * Math.PI) / 180;
    const def: EnemyProjectileDef = {
      speed: ab.projectileSpeed ?? 300,
      damage: this.bossAbilityDamage(e, ab),
      range: ab.projectileRange ?? 300,
      hitRadius: ab.hitRadius ?? 12,
      cooldownMs: 0,
    };
    for (let i = 0; i < n; i++) {
      const frac = n === 1 ? 0 : i / (n - 1) - 0.5;
      const ang = baseAng + frac * spread;
      this.spawnEnemyProjectile(
        e,
        e.x + Math.cos(ang) * 100,
        e.y + Math.sin(ang) * 100,
        def,
        ab.onHitStatus,
      );
    }
  }

  /**
   * feature 5.2 — `melee` (Warlord `cleave`): an instant short-range hit on every
   * living hero within `radius` of the boss body, `dealEnemyDamage` +
   * `onHitStatus`. The rotation fallback between the cast abilities' cooldowns.
   */
  private bossMelee(e: SimEnemy, ab: BossAbilityDef, target: SimPlayer | null): void {
    const reach = ab.radius ?? 0;
    this.fx.push({
      kind: 'enemyMelee',
      x: e.x,
      y: e.y,
      tx: target?.x ?? e.x,
      ty: target?.y ?? e.y,
    });
    for (const p of this.players.values()) {
      if (!World.isLiving(p)) continue;
      if (Math.hypot(p.x - e.x, p.y - e.y) - p.radius - e.radius > reach) continue;
      this.dealEnemyDamage(p, this.bossAbilityDamage(e, ab));
      const s = ab.onHitStatus;
      if (s) applyStatus(p, s.kind, s.durationMs, s.magnitude, String(e.id), s.tickIntervalMs);
      this.fx.push({ kind: 'hit', x: p.x, y: p.y });
    }
  }

  /**
   * feature 5.2 / 5.3c / 5.4 (§E15) — the wave-30 final boss (`onDeath:
   * "winRun"`) has died. `creditKill` has already paid its (large) rewards;
   * here we set the `runWon` flag (mirrored to `GameState.runWon` for the
   * client) and clear the wave. The end-of-tick `enemies.size === 0` check in
   * `tickMatch` sees `runWon` and routes to the terminal `victory` phase
   * instead of `enterWaveBreak()`.
   */
  private winRun(): void {
    this.runWon = true;
    for (const other of [...this.enemies.values()]) this.enemies.delete(other.id);
  }

  /**
   * Enter the between-waves break (criterion 21). Living players are topped up to
   * full HP/mana; dead players are revived at `reviveHpFactor` of max HP *and*
   * mana (feature 5.6: gated by the shared `revivalTokens` budget on hard/
   * brutal — see below); every player's ability + basic-attack cooldowns reset
   * to 0. This supersedes the pilot's end-of-break revival — `startNextWave`
   * does nothing extra. Regen (criterion 5) keeps running during the break.
   */
  private enterWaveBreak(): void {
    this.castQueue = [];
    // feature 1.5 — committed windups, live telegraphs and projectiles all end
    // with the wave (criteria 8 / 13).
    this.clearScheduledEffects();
    // feature 5.2 (§D14) — a boss fight's bespoke pillar obstacles end with the
    // wave; pathfinding + collision revert to the base arena set.
    resetActiveObstacles();
    // feature 2.2 (criterion 8) — a fresh break starts with nobody ready.
    for (const p of this.players.values()) p.ready = false;
    const f = DATA.players.reviveHpFactor;
    for (const p of this.players.values()) {
      this.resetToSpawn(p, false);
      if (!p.cls) continue;
      // feature 2.1 (criterion A3) — wave-clear gold bonus, paid only to players
      // still alive when the wave ended (checked before the revive step below).
      // `this.wave` is the wave just cleared (startNextWave increments later).
      // feature 2.2 (criterion A3) — never paid for the pre-wave-1 break (wave 0).
      if (this.wave > 0 && !p.dead) {
        p.gold += DATA.economy.waveClearGoldBase + this.wave * DATA.economy.waveClearGoldPerWave;
      }
      if (p.dead) {
        // feature 5.6 — on hard/brutal (`revivalTokens !== null`) the wave-end
        // revive is gated by the shared team budget: spend 1 token per revive
        // (Map insertion order — the natural iteration order of this loop), and
        // once the budget hits 0 the player stays dead (no hp/mana change) for
        // the rest of the run. `null` (easy/normal/medium) is always unlimited,
        // byte-identical to pre-5.6.
        if (this.revivalTokens === null || this.revivalTokens > 0) {
          if (this.revivalTokens !== null) this.revivalTokens -= 1;
          p.dead = false;
          p.hp = Math.floor(p.maxHp * f);
          p.mana = Math.floor(p.maxMana * f);
        }
      } else {
        p.hp = p.maxHp;
        p.mana = p.maxMana;
      }
      p.cooldown = 0;
      p.abilityCd = [0, 0, 0, 0];
      // Statuses + the Whirlwind channel end on entering the break (criterion 6);
      // enemies (and their debuffs) are already gone by now.
      clearStatuses(p);
      p.whirlwindTickTimer = 0;
    }
    this.vacuumGroundItems();
    this.phase = 'wavebreak';
    this.countdown = DATA.waves.waveBreakSeconds;
  }

  private tickWaveBreak(dt: number): void {
    this.applyRegen(dt);
    // feature 4.1 (criterion 6) — XP is not awarded during the break, but the
    // level settle is cheap + idempotent so it runs here too for consistency.
    this.applyLevelUps();
    // feature 3.4 — the wave-clear vacuum in `enterWaveBreak` already sweeps the
    // floor into inventories with room, so this is normally a no-op during the
    // break (players are also frozen at spawn). Kept as a harmless safety net
    // that starts working the instant break-time movement is ever added.
    this.tickGroundItemPickups();
    this.countdown -= dt;
    if (this.countdown <= 0) {
      this.endWaveBreak();
      return;
    }
    // feature 2.2 (criterion B7) — all-ready skip: once every voting player (has
    // a class + is connected) is ready, end the break at once. A disconnected
    // player is not a voter, so does not block (criterion 9). A solo player
    // readying ends it immediately. Voters iterated in Map insertion order.
    const voters = [...this.players.values()].filter((p) => p.cls && p.connected);
    if (voters.length > 0 && voters.every((p) => p.ready)) {
      this.endWaveBreak();
    }
  }

  /**
   * feature 2.2 (criterion 4) — end the current wavebreak and roll into the next
   * wave. Shared by both exit paths: the 3-minute timer expiring and the
   * all-ready skip. `startNextWave` resets every player's `ready` flag.
   */
  private endWaveBreak(): void {
    this.countdown = 0;
    this.phase = 'match';
    this.startNextWave();
  }

  /**
   * feature 2.2 (criterion B6) — set / toggle the sender's wavebreak "Ready"
   * vote. Toggles when `value` is omitted. Ignored outside `wavebreak`.
   */
  setReady(id: string, value?: boolean): void {
    if (this.phase !== 'wavebreak') return;
    const p = this.players.get(id);
    if (!p) return;
    p.ready = value === undefined ? !p.ready : value;
  }

  /**
   * HP + mana regen (criterion 5): per-second rates, tick-scaled, capped at the
   * respective max, skipped for dead players. Also skipped at exactly 0 HP so
   * regen never resurrects a player before the death check marks them dead.
   */
  private applyRegen(dt: number): void {
    for (const p of this.players.values()) {
      if (p.dead || !p.cls || p.hp <= 0) continue;
      if (p.hp < p.maxHp) p.hp = Math.min(p.maxHp, p.hp + p.hpRegenPerSec * dt);
      if (p.mana < p.maxMana) p.mana = Math.min(p.maxMana, p.mana + p.manaRegenPerSec * dt);
    }
  }

  private tickMatch(dt: number): void {
    const dtMs = dt * 1000;

    // 0. resolve queued ability casts deterministically (criterion 15).
    this.consumeCasts();

    // 0a. feature 1.5 — resolve any windup ground-AoE whose delay has elapsed
    // (before movement / combat), then advance projectiles, then age the
    // remaining telegraphs. Fixed order, no RNG (criteria B / C).
    this.resolvePendingEffects();
    this.stepProjectiles(dt);
    this.tickTelegraphs(dtMs);

    // 0b. tick every timed status effect at a fixed deterministic point, before
    // movement / combat resolution (criterion 5). Insertion order, no RNG.
    for (const p of this.players.values()) tickStatuses(p, dtMs);
    for (const e of this.enemies.values()) tickStatuses(e, dtMs);

    // 0c. periodic statuses (feature 1.3b): `regen` heals a player each interval,
    // `dot` damages an enemy each interval (armor-mitigated, kill credit to the
    // source). Deterministic: players then enemies, Map insertion order.
    for (const p of this.players.values()) {
      if (p.dead || !p.cls) continue;
      tickPeriodicStatuses(p, dtMs, (s) => {
        if (s.kind === 'regen') {
          this.dealHeal(p, s.magnitude);
          this.fx.push({ kind: 'abilityRegrowth', x: p.x, y: p.y });
        } else if (s.kind === 'dot' && p.hp > 0) {
          // feature 5.1b — an enemy `dot` (witchHound bite) burns the hero each
          // interval. Flat, armor-independent (it is a poison, not a hit); the
          // hit-time shield / DR already interposed on the bite damage itself.
          p.hp -= s.magnitude;
          this.fx.push({ kind: 'hit', x: p.x, y: p.y });
        }
      });
    }
    for (const e of [...this.enemies.values()]) {
      tickPeriodicStatuses(e, dtMs, (s) => {
        if (s.kind !== 'dot') return;
        // feature 5.2 (§C11) — a boss shrugs off dot ticks while invulnerable.
        if (this.bossDamageBlocked(e)) return;
        e.hp -= Math.max(1, s.magnitude - e.armor);
        this.fx.push({ kind: 'hit', x: e.x, y: e.y });
        // feature 2.1 — even a dot whose source disconnected still awards its
        // shared XP to the living; gold only if the source is still around.
        const src = s.sourceId ? this.players.get(s.sourceId) : undefined;
        this.creditKill(src, e);
      });
    }

    // 0d. feature 5.3a — floor hazards: age each zone and apply damage / refresh
    // the standing-in-it slow. Runs after the status pass so a slow it applies
    // this tick is not immediately decremented, and before movement so that slow
    // scales this tick's step. Deterministic, no RNG (§A7).
    this.tickHazards(dtMs);

    // 1. players — cooldowns, then status-gated movement, then Whirlwind ticks.
    for (const p of this.players.values()) {
      if (p.dead || !p.cls) continue;
      if (p.cooldown > 0) p.cooldown -= dtMs;
      for (let i = 0; i < p.abilityCd.length; i++) {
        if (p.abilityCd[i]! > 0) p.abilityCd[i] = Math.max(0, p.abilityCd[i]! - dtMs);
      }

      if (hasStatus(p, 'stun')) {
        // stunned: cannot move or path (criterion 4).
        p.path = null;
        p.pathIndex = 0;
        p.vx = 0;
        p.vy = 0;
        p.moving = false;
      } else {
        // slow scales effective move speed; magnitude 0 when not slowed.
        // feature 2.3 — `baseMoveSpeed` already includes any shop `moveSpeed`
        // bonus (criterion 9).
        const slow = statusMagnitude(p, 'slow');
        p.speed = p.baseMoveSpeed * Math.max(0, 1 - slow);
        p.moving = stepLocomotion(p, p.input.moveX, p.input.moveY, dt);
        p.facing = angleOf(p.vx, p.vy, p.facing);
      }

      if (hasStatus(p, 'whirlwind')) this.tickWhirlwind(p, dtMs);
    }

    // 2. player vs player separation
    const alive = [...this.players.values()].filter((p) => !p.dead && p.cls);
    for (let i = 0; i < alive.length; i++) {
      for (let j = i + 1; j < alive.length; j++) {
        separateBodies(alive[i]!, alive[j]!);
      }
    }

    // 3. enemies (sweep any that reached 0 HP, then run AI). feature 5.3b — a
    // downed linked twin sits at 0 hp on purpose; it is NOT swept here (its
    // death runs through `tickLinkedBosses`).
    for (const e of [...this.enemies.values()]) {
      if (e.hp <= 0 && !e.downed) {
        this.enemies.delete(e.id);
        continue;
      }
      this.tickEnemy(e, dt, dtMs);
    }

    // 4. enemy vs enemy separation (keeps the swarm from stacking on one pixel)
    const es = [...this.enemies.values()];
    for (let i = 0; i < es.length; i++) {
      for (let j = i + 1; j < es.length; j++) {
        separateBodies(es[i]!, es[j]!);
      }
    }

    // 4b. keep everyone out of walls after separation nudges
    for (const p of alive) {
      const c = { x: p.x, y: p.y, r: p.radius };
      resolveAgainstArena(c);
      p.x = c.x;
      p.y = c.y;
    }
    for (const e of es) {
      const c = { x: e.x, y: e.y, r: e.radius };
      resolveAgainstArena(c);
      e.x = c.x;
      e.y = c.y;
    }

    // 4c. feature 3.4 — ground-item pickups, now that every body position is
    // settled for the tick. Deterministic (no RNG); an item dropped by a kill
    // earlier this tick is eligible immediately.
    this.tickGroundItemPickups();

    // 5. player auto-attacks
    for (const p of this.players.values()) {
      if (p.dead || !p.cls) continue;
      if (p.moving) continue; // no attacking while moving
      if (p.cooldown > 0) continue;
      if (hasStatus(p, 'stun')) continue; // stun blocks the auto attack (criterion 4)
      if (hasStatus(p, 'whirlwind')) continue; // no auto attack while channelling (criterion 16)
      this.tryPlayerAttack(p);
    }

    // 6. deaths / regen / wave end / game over
    for (const p of this.players.values()) {
      if (!p.dead && p.hp <= 0) {
        p.hp = 0;
        p.dead = true;
        // feature 2.1 (criterion A2) — losing a fraction of current gold on death.
        p.gold = Math.max(0, p.gold - Math.floor(p.gold * DATA.economy.goldLossOnDeathFrac));
        p.path = null;
        // Statuses + the Whirlwind channel end on death (criterion 6).
        clearStatuses(p);
        p.whirlwindTickTimer = 0;
      }
    }

    // Regen after the death check so it never resurrects a player (criterion 5).
    this.applyRegen(dt);

    // feature 5.3b — advance the linked-boss revive timers, revive or kill the
    // group as the rules dictate. Runs before `applyLevelUps` so a group-kill's
    // shared XP settles this tick, and before the empty-wave check so the kill
    // rolls straight into `wavebreak`.
    this.tickLinkedBosses(dtMs);

    // feature 4.1 (criterion 6) — settle levels once all XP for the tick is in.
    this.applyLevelUps();

    const anyAlive = [...this.players.values()].some((p) => p.cls && !p.dead);
    if (!anyAlive && this.players.size > 0) {
      this.phase = 'gameover';
      return;
    }

    if (this.enemies.size === 0) {
      // feature 5.4 — the wave-30 final boss's `winRun()` set `runWon`; route
      // to the terminal `victory` phase instead of the next wave break.
      if (this.runWon) {
        this.phase = 'victory';
        return;
      }
      this.enterWaveBreak();
    }
  }

  /**
   * feature 5.1a — per-tick enemy AI, dispatched on `e.ai`:
   * - `melee` — the pilot goblin behaviour (chase / path / melee on cooldown),
   *   unchanged.
   * - `brute` — `melee` plus a `knockback` shove of the hero on every landed hit
   *   (the catalog also gives it a slower `attackSpeed`).
   * - `swarm` — `melee` with the catalog's high `speed` (just a stat).
   * - `kamikaze` — `tickKamikaze`: ignores `attackRange`, rushes the nearest
   *   living hero, explodes on contact or on death.
   *
   * The shared prologue (stun freeze, taunt / nearest-hero target pick, the
   * no-target idle) runs for every archetype first.
   */
  private tickEnemy(e: SimEnemy, dt: number, dtMs: number): void {
    if (e.cooldown > 0) e.cooldown -= dtMs;
    // feature 5.1b — the `caster` heal timer shares the per-tick decrement.
    if (e.healCooldown > 0) e.healCooldown -= dtMs;

    // feature 5.2 — a boss runs its own scripted AI (phases + rotation + cast
    // bar + bespoke abilities). Everything below is the trash-mob dispatch.
    if (e.boss) {
      this.tickBoss(e, dt, dtMs);
      return;
    }

    // stun freezes the enemy entirely (criterion 4/7) — this early return also
    // blocks all 5.1b firing / casting / healing while stunned (criterion 18).
    if (hasStatus(e, 'stun')) {
      e.vx = 0;
      e.vy = 0;
      e.moving = false;
      e.path = null;
      return;
    }

    // taunt forces the target onto the taunting player while that player is
    // alive + valid; otherwise fall back to the nearest living player (crit. 4).
    let target: SimPlayer | null = null;
    const tauntId = statusSource(e, 'taunt');
    if (tauntId) {
      const forced = this.players.get(tauntId);
      if (forced && !forced.dead && forced.cls) target = forced;
    }
    if (!target) {
      let best = Infinity;
      for (const p of this.players.values()) {
        if (p.dead || !p.cls) continue;
        const d = (p.x - e.x) ** 2 + (p.y - e.y) ** 2;
        if (d < best) {
          best = d;
          target = p;
        }
      }
    }
    e.targetId = target?.id ?? null;

    if (!target) {
      e.vx = 0;
      e.vy = 0;
      e.moving = false;
      return;
    }

    // feature 5.1a — the kamikaze ignores attackRange entirely and has its own
    // rush-and-detonate loop.
    if (e.ai === 'kamikaze') {
      this.tickKamikaze(e, dt, target);
      return;
    }

    // feature 5.1b — ranged / caster kite to `preferredRange` and attack from
    // afar (hostile projectile / hostile ground-AoE + heal). No melee.
    if (e.ai === 'ranged') {
      this.tickRangedEnemy(e, dt, target);
      return;
    }
    if (e.ai === 'caster') {
      this.tickCasterEnemy(e, dt, target);
      return;
    }

    const dist = Math.hypot(target.x - e.x, target.y - e.y);
    const edge = dist - e.radius - target.radius;

    if (edge <= e.attackRange) {
      // in range: stop and attack
      e.vx = 0;
      e.vy = 0;
      e.moving = false;
      e.path = null;
      e.facing = Math.atan2(target.y - e.y, target.x - e.x);
      if (e.cooldown <= 0) {
        this.dealEnemyDamage(target, e.damage);
        // feature 5.1b (criterion 5) — an elite melee hit (skeletonKnight stun,
        // witchHound dot) applies its status to the hero, regardless of the
        // shield / DR that interposed on the damage.
        this.applyEnemyOnHitStatus(e, target);
        e.cooldown = e.attackSpeed;
        this.fx.push({ kind: 'enemyMelee', x: e.x, y: e.y, tx: target.x, ty: target.y });
        this.fx.push({ kind: 'hit', x: target.x, y: target.y });
        // feature 5.1a (criterion 11) — brute: on a landed hit that did not kill
        // the hero, shove them `knockback` px directly away (arena-collided;
        // shares `knockbackBody` with Warrior Charge).
        if (e.ai === 'brute' && e.knockback > 0 && target.hp > 0) {
          this.knockbackBody(target, target.x - e.x, target.y - e.y, e.knockback);
        }
      }
      return;
    }

    // chase: repath periodically or when line of sight is clear
    e.repathTimer -= dt;
    if (e.repathTimer <= 0 || !e.path) {
      e.repathTimer = 0.4;
      const path = findPath({ x: e.x, y: e.y }, { x: target.x, y: target.y });
      e.path = path.length > 0 ? path : null;
      e.pathIndex = 0;
    }
    // slow scales the enemy's effective move speed (criterion 4/7).
    const slow = statusMagnitude(e, 'slow');
    e.speed = e.baseSpeed * Math.max(0, 1 - slow);
    e.moving = stepLocomotion(e, 0, 0, dt);
    e.facing = angleOf(e.vx, e.vy, e.facing);
  }

  /**
   * feature 5.1a (criterion 11) — kamikaze (`impBomber`) AI. Ignores
   * `attackRange`; always paths to the nearest living hero (`target`, already
   * resolved by `tickEnemy`). When a hero is within `contactRadius` it
   * detonates on contact: `impExplosion` then removal with **no kill credit**
   * (it was not "killed"). A kamikaze that instead reaches 0 HP detonates from
   * `creditKill` (normal gold / XP / drop credit) — see there.
   */
  private tickKamikaze(e: SimEnemy, dt: number, target: SimPlayer): void {
    // Centre-to-centre distance test (`contactRadius` ~= the two bodies'
    // radii summed). This only works because the sim has NO hero<->enemy body
    // separation — the imp is free to walk its centre onto the hero's. If a
    // "push players off enemies" step is ever added, this must switch to an
    // edge-gap test or imps stop detonating on contact.
    if (Math.hypot(target.x - e.x, target.y - e.y) <= e.contactRadius) {
      this.impExplosion(e);
      this.enemies.delete(e.id);
      return;
    }
    e.repathTimer -= dt;
    if (e.repathTimer <= 0 || !e.path) {
      e.repathTimer = 0.4;
      const path = findPath({ x: e.x, y: e.y }, { x: target.x, y: target.y });
      e.path = path.length > 0 ? path : null;
      e.pathIndex = 0;
    }
    const slow = statusMagnitude(e, 'slow');
    e.speed = e.baseSpeed * Math.max(0, 1 - slow);
    e.moving = stepLocomotion(e, 0, 0, dt);
    e.facing = angleOf(e.vx, e.vy, e.facing);
  }

  // ------------------------------------------------ feature 5.1b ranged / caster

  /**
   * feature 5.1b (criterion 16) — `ranged` AI (`goblinArcher` / `spiderSpitter`).
   * Kite to `preferredRange` off the nearest living hero (`target`, already
   * resolved by `tickEnemy`); when the hero is within `projectile.range` and the
   * fire cooldown (`e.cooldown`) is up, spawn a hostile projectile at the hero's
   * current position and re-arm for `projectile.cooldownMs`. No melee.
   */
  private tickRangedEnemy(e: SimEnemy, dt: number, target: SimPlayer): void {
    const dist = Math.hypot(target.x - e.x, target.y - e.y);
    this.kiteEnemy(e, dt, target, dist);
    e.facing = Math.atan2(target.y - e.y, target.x - e.x);
    const pr = e.projectile;
    if (pr && e.cooldown <= 0 && dist <= pr.range) {
      this.spawnEnemyProjectile(e, target.x, target.y);
      e.cooldown = pr.cooldownMs;
    }
  }

  /**
   * feature 5.1b (criterion 17) — `caster` AI (`goblinShaman`). Kites like
   * `ranged`. Two independent cooldowns: `e.cooldown` gates the hostile ground
   * AoE (scheduled at the target hero's position when they are within
   * `groundAoe.castRange`), `e.healCooldown` gates the heal pulse (tops up every
   * enemy within `heal.radius` that is below max HP, by `heal.amount`). No melee,
   * no projectile.
   */
  private tickCasterEnemy(e: SimEnemy, dt: number, target: SimPlayer): void {
    const dist = Math.hypot(target.x - e.x, target.y - e.y);
    this.kiteEnemy(e, dt, target, dist);
    e.facing = Math.atan2(target.y - e.y, target.x - e.x);

    const g = e.groundAoe;
    if (g && e.cooldown <= 0 && dist <= g.castRange) {
      this.schedulePendingEnemyAoe(e, target.x, target.y);
      e.cooldown = g.cooldownMs;
    }

    const h = e.heal;
    if (h && e.healCooldown <= 0) {
      let healed = false;
      for (const other of this.enemies.values()) {
        if (other.hp >= other.maxHp) continue;
        if (Math.hypot(other.x - e.x, other.y - e.y) > h.radius) continue;
        other.hp = Math.min(other.maxHp, other.hp + h.amount);
        healed = true;
      }
      if (healed) {
        this.fx.push({ kind: 'enemyHeal', x: e.x, y: e.y });
        e.healCooldown = h.cooldownMs;
      }
    }
  }

  /**
   * feature 5.1b — move `e` toward / away from `target` to hold `preferredRange`:
   * approach when `dist > preferredRange`, retreat when
   * `dist < preferredRange * 0.7`, otherwise hold. Direct locomotion (no path);
   * arena-collided by `stepLocomotion`. `slow` scales the effective speed exactly
   * as for the melee archetypes. Deterministic, no RNG.
   */
  private kiteEnemy(e: SimEnemy, dt: number, target: SimPlayer, dist: number): void {
    const slow = statusMagnitude(e, 'slow');
    e.speed = e.baseSpeed * Math.max(0, 1 - slow);
    const nx = dist > 1e-6 ? (target.x - e.x) / dist : 0;
    const ny = dist > 1e-6 ? (target.y - e.y) / dist : 0;
    let dirX = 0;
    let dirY = 0;
    if (dist > e.preferredRange) {
      dirX = nx;
      dirY = ny;
    } else if (dist < e.preferredRange * 0.7) {
      dirX = -nx;
      dirY = -ny;
    }
    e.path = null;
    e.pathIndex = 0;
    e.moving = stepLocomotion(e, dirX, dirY, dt);
    e.facing = angleOf(e.vx, e.vy, e.facing);
  }

  /** feature 5.1b — apply an enemy's `onHitStatus` to a hero it just hit
   * (`sourceId` = the enemy id as a string). No-op when the enemy has none. */
  private applyEnemyOnHitStatus(e: SimEnemy, target: SimPlayer): void {
    const s = e.onHitStatus;
    if (!s) return;
    applyStatus(target, s.kind, s.durationMs, s.magnitude, String(e.id), s.tickIntervalMs);
  }

  /**
   * feature 5.1b (criteria 7–9) — spawn one hostile projectile from `e` toward
   * `(tx, ty)`. Speed / damage / range / hitRadius come from the enemy's
   * `projectile` def; `owner` is `'enemy'`, `casterId` is `String(e.id)`. It
   * does not pierce (expires on the first hero hit / at range / on a wall).
   */
  private spawnEnemyProjectile(
    e: SimEnemy,
    tx: number,
    ty: number,
    def?: EnemyProjectileDef,
    onHit?: EnemyOnHitStatus,
  ): void {
    // feature 5.2 — a boss `projectileVolley` passes an explicit `def` (the boss
    // has no catalog `e.projectile`); every 5.1b caller omits it and uses the
    // enemy's own `projectile` / `onHitStatus` as before.
    const pr = def ?? e.projectile;
    if (!pr) return;
    let dx = tx - e.x;
    let dy = ty - e.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) {
      dx = Math.cos(e.facing);
      dy = Math.sin(e.facing);
    } else {
      dx /= len;
      dy /= len;
    }
    this.projectiles.set(this.nextProjectileId, {
      id: this.nextProjectileId,
      x: e.x,
      y: e.y,
      vx: dx * pr.speed,
      vy: dy * pr.speed,
      owner: 'enemy',
      casterId: String(e.id),
      abilityId: '',
      remainingRange: pr.range,
      hitRadius: pr.hitRadius,
      damage: pr.damage,
      onHitStatus: onHit ?? e.onHitStatus,
      pierced: new Set<number>(),
    });
    this.nextProjectileId++;
    this.fx.push({ kind: 'enemyShoot', x: e.x, y: e.y, tx, ty });
  }

  /**
   * feature 5.1b (criteria 12–13) — schedule a hostile delayed ground-AoE from
   * `e` at `(x, y)`. Committed on cast: the pending entry + telegraph resolve
   * even if `e` dies during the windup. `windupMs` / `radius` / `damage` come
   * from the enemy's `groundAoe` def; the telegraph is flagged `hostile`.
   */
  private schedulePendingEnemyAoe(e: SimEnemy, x: number, y: number): void {
    const g = e.groundAoe;
    if (!g) return;
    const telegraphId = this.nextTelegraphId++;
    const resolveTick = this.serverTick + Math.ceil(g.windupMs / TICK_MS);
    this.pendingEffects.push({
      source: 'enemy',
      kind: 'aoe',
      resolveTick,
      enemyId: String(e.id),
      x,
      y,
      radius: g.radius,
      damage: g.damage,
      onHitStatus: e.onHitStatus,
      telegraphId,
    });
    this.telegraphs.set(telegraphId, {
      id: telegraphId,
      x,
      y,
      radius: g.radius,
      remainingMs: g.windupMs,
      totalMs: g.windupMs,
      kind: e.etype,
      hostile: true,
    });
  }

  /**
   * feature 5.1b (criterion 12) — resolve one hostile ground-AoE at its locked
   * point: `dealEnemyDamage` + the optional `onHitStatus` to every living hero
   * within `radius`. Runs even if the source enemy is gone. Heroes iterated in
   * Map insertion order; no RNG.
   */
  private resolveEnemyAoe(pe: EnemyAoePendingEffect): void {
    this.fx.push({ kind: 'enemyExplode', x: pe.x, y: pe.y, radius: pe.radius });
    for (const p of this.players.values()) {
      if (!World.isLiving(p)) continue;
      if (Math.hypot(p.x - pe.x, p.y - pe.y) > pe.radius) continue;
      this.dealEnemyDamage(p, pe.damage);
      const s = pe.onHitStatus;
      if (s) applyStatus(p, s.kind, s.durationMs, s.magnitude, pe.enemyId, s.tickIntervalMs);
      // feature 5.2 — boss `groundSlam` radial knockback: shove every surviving
      // hero directly away from the blast centre (arena-collided, shares
      // `knockbackBody` with Warrior Charge / the 5.1a brute). feature 5.3b —
      // Gale Twins `gust`: shove away from the ARENA CENTRE toward the lethal
      // edge instead.
      if (pe.knockback && pe.knockback > 0 && p.hp > 0) {
        const originX = pe.knockbackFromCentre ? ARENA.width / 2 : pe.x;
        const originY = pe.knockbackFromCentre ? ARENA.height / 2 : pe.y;
        this.knockbackBody(p, p.x - originX, p.y - originY, pe.knockback);
      }
      this.fx.push({ kind: 'hit', x: p.x, y: p.y });
    }
  }

  /**
   * feature 5.2 — resolve a boss `dashLine` (Warlord `warlordCharge`): the boss
   * dashes from its current position straight to the locked destination; every
   * living hero within the corridor half-width (`pe.radius`) of that line takes
   * `damage` + `knockback` along the dash direction (the Warrior Charge corridor
   * scan, reused). The boss then teleports to the destination (arena-collided)
   * — unless `pe.noMove` (feature 5.3b — Gale Twins `tornadoLine`), in which case
   * the sweep still damages the corridor but the boss stays put.
   * Fizzles if the boss died during the telegraph. Heroes iterated in Map
   * insertion order; no RNG.
   */
  private resolveBossDash(pe: BossDashPendingEffect): void {
    const e = this.enemies.get(Number(pe.enemyId));
    if (!e) return;
    const fromX = e.x;
    const fromY = e.y;
    let dx = pe.x - fromX;
    let dy = pe.y - fromY;
    const dist = Math.hypot(dx, dy);
    if (dist < 1e-6) {
      dx = Math.cos(e.facing);
      dy = Math.sin(e.facing);
    } else {
      dx /= dist;
      dy /= dist;
    }
    const travel = dist;
    const half = pe.radius;
    for (const p of this.players.values()) {
      if (!World.isLiving(p)) continue;
      const relX = p.x - fromX;
      const relY = p.y - fromY;
      const along = relX * dx + relY * dy;
      if (along < -p.radius || along - p.radius > travel) continue;
      const perp = Math.abs(relX * -dy + relY * dx);
      if (perp > half + p.radius) continue;
      this.dealEnemyDamage(p, pe.damage);
      const s = pe.onHitStatus;
      if (s) applyStatus(p, s.kind, s.durationMs, s.magnitude, pe.enemyId, s.tickIntervalMs);
      if (p.hp > 0) this.knockbackBody(p, dx, dy, pe.knockback);
      this.fx.push({ kind: 'hit', x: p.x, y: p.y });
    }
    // feature 5.3c — `lavaSweep`: leave a trail of lingering hazard circles along
    // the dash path (deterministic — fixed spacing, monotonic hazard id, no RNG).
    if (pe.lingerHazardKind) {
      const hr = pe.lingerHazardRadius ?? 60;
      const steps = Math.max(1, Math.round(travel / hr));
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        this.spawnHazard(
          fromX + dx * travel * t,
          fromY + dy * travel * t,
          hr,
          pe.lingerHazardKind,
          pe.lingerHazardMagnitude ?? 0,
          pe.lingerHazardDurationMs ?? 0,
          pe.enemyId,
        );
      }
    }
    if (pe.noMove) {
      e.facing = Math.atan2(dy, dx);
      this.fx.push({ kind: 'abilityCharge', x: fromX, y: fromY, tx: pe.x, ty: pe.y });
      return;
    }
    const land = { x: pe.x, y: pe.y, r: e.radius };
    resolveAgainstArena(land);
    e.x = land.x;
    e.y = land.y;
    e.vx = 0;
    e.vy = 0;
    e.path = null;
    e.pathIndex = 0;
    e.facing = Math.atan2(dy, dx);
    this.fx.push({ kind: 'abilityCharge', x: fromX, y: fromY, tx: e.x, ty: e.y });
  }

  /**
   * feature 5.3a (§B9) — resolve a boss `hazardSpawn` circle: drop a floor
   * hazard at the locked point (`spawnHazard`). Runs even if the boss is gone.
   */
  private resolveHazardSpawn(pe: HazardPendingEffect): void {
    this.fx.push({ kind: 'enemyExplode', x: pe.x, y: pe.y, radius: pe.radius });
    this.spawnHazard(
      pe.x,
      pe.y,
      pe.radius,
      pe.hazardKind,
      pe.hazardMagnitude,
      pe.hazardDurationMs,
      pe.enemyId,
    );
  }

  /**
   * feature 5.1a (criteria 11 / 12) — apply `raw` enemy damage to a hero
   * through the exact mitigation chain the goblin melee has always used: flat
   * `armor` (+ `armorBonus` status) at 0.5 efficiency, then the
   * `damageReduction` multiplier, then the `shield` absorb pool (dropped when
   * spent, still expires on its own timer). Shared by goblin / brute melee and
   * the kamikaze explosion so every hero-damage interposer behaves identically.
   */
  private dealEnemyDamage(target: SimPlayer, raw: number): void {
    const armor = target.armor + statusMagnitude(target, 'armorBonus');
    const dr = statusMagnitude(target, 'damageReduction');
    let dmg = Math.max(1, raw - armor * 0.5) * (1 - dr);
    const shield = target.statuses.find((s) => s.kind === 'shield');
    if (shield) {
      const absorbed = Math.min(shield.magnitude, dmg);
      shield.magnitude -= absorbed;
      dmg -= absorbed;
      if (shield.magnitude <= 0) {
        const si = target.statuses.indexOf(shield);
        if (si >= 0) target.statuses.splice(si, 1);
      }
    }
    target.hp -= dmg;
  }

  /**
   * feature 5.1a — the kamikaze blast: an `enemyExplode` fx plus
   * `explosionDamage` (via `dealEnemyDamage`, so armor / shield / DR interpose)
   * to every living hero within `explosionRadius`. Does NOT remove the enemy or
   * award any credit — the caller (`tickKamikaze` contact / `creditKill` death)
   * handles that. Heroes iterated in Map insertion order; no RNG.
   */
  private impExplosion(e: SimEnemy): void {
    this.fx.push({ kind: 'enemyExplode', x: e.x, y: e.y, radius: e.explosionRadius });
    for (const p of this.players.values()) {
      if (p.dead || !p.cls) continue;
      if (Math.hypot(p.x - e.x, p.y - e.y) > e.explosionRadius) continue;
      this.dealEnemyDamage(p, e.explosionDamage);
    }
  }

  /**
   * Knock `body` `dist` px along `(dirX, dirY)` (need not be unit; a ~zero
   * vector falls back to the body's `facing`), resolved against arena
   * collision, cancelling its current velocity / path. Shared by Warrior
   * Charge's on-hit knockback (an enemy, along the dash line) and the feature
   * 5.1a brute's on-hit knockback (a hero, directly away from the brute).
   */
  private knockbackBody(
    body: Locomotor & { facing: number },
    dirX: number,
    dirY: number,
    dist: number,
  ): void {
    let nx = dirX;
    let ny = dirY;
    const len = Math.hypot(nx, ny);
    if (len < 1e-6) {
      nx = Math.cos(body.facing);
      ny = Math.sin(body.facing);
    } else {
      nx /= len;
      ny /= len;
    }
    const c = { x: body.x + nx * dist, y: body.y + ny * dist, r: body.radius };
    resolveAgainstArena(c);
    body.x = c.x;
    body.y = c.y;
    body.vx = 0;
    body.vy = 0;
    body.path = null;
    body.pathIndex = 0;
  }

  private tryPlayerAttack(p: SimPlayer): void {
    let target: SimEnemy | null = null;
    let best = Infinity;
    for (const e of this.enemies.values()) {
      const d = Math.hypot(e.x - p.x, e.y - p.y) - e.radius - p.radius;
      if (d < best) {
        best = d;
        target = e;
      }
    }
    if (!target || best > p.attackRange) return;

    // feature 1.4 — damageBonus raises the swing (pre-armor, matching
    // `abilityDamage`); attackSpeedBonus shortens the recovery.
    const dmgBonus = 1 + statusMagnitude(p, 'damageBonus');
    const dmg = Math.max(1, p.damage * dmgBonus - target.armor);
    // feature 5.2 (§C11) — a boss ignores auto-attack damage while invulnerable;
    // the swing still costs its recovery time so the attacker is not rewarded.
    const blocked = this.bossDamageBlocked(target);
    if (!blocked) target.hp -= dmg;
    p.cooldown = p.attackSpeed / (1 + statusMagnitude(p, 'attackSpeedBonus'));
    p.facing = Math.atan2(target.y - p.y, target.x - p.x);

    this.fx.push({
      kind: p.attackStyle === 'ranged' ? 'ranged' : 'melee',
      x: p.x,
      y: p.y,
      tx: target.x,
      ty: target.y,
    });
    this.fx.push({ kind: 'hit', x: target.x, y: target.y });

    if (!blocked) this.creditKill(p, target);
  }

  /**
   * A player who counts as alive for reward / regen purposes this tick: has a
   * class, is not marked `dead`, and is not sitting at 0 HP waiting for the
   * step-6 death sweep. Matches the guards in `applyRegen` / `dealHeal`.
   */
  private static isLiving(p: SimPlayer): boolean {
    return !!p.cls && !p.dead && p.hp > 0;
  }

  /**
   * If `e` is at 0 HP, remove it and pay out its rewards (feature 2.1):
   * - `goldReward` goes to the killer **only** (gold stays individual), and only
   *   if that killer is still alive (criterion A4 — a dead player earns no gold,
   *   including from a windup / DoT they started before dying). A projectile /
   *   windup kill whose caster is gone (`killer === undefined`) pays no gold.
   * - `xpReward` is **shared**: every living player gets the full amount (not
   *   split). The killer, if living, is one of them — awarded once by the loop.
   *
   * Determinism: players iterated in Map insertion order; no RNG.
   */
  private creditKill(killer: SimPlayer | undefined, e: SimEnemy): void {
    if (e.hp > 0) return;
    // feature 5.3b (§B) — a linked boss does NOT die here. It enters the downed
    // state (or, if already downed, just records the last hitter); the real
    // death gate is "every linked twin downed at once", handled per-tick by
    // `tickLinkedBosses`. The enemy is NOT removed and no rewards are paid.
    if (e.boss && e.linkGroupId && DATA.bosses[e.bossId ?? '']?.linkMode === 'revive') {
      e.lastHitterId = killer?.id ?? e.lastHitterId;
      this.downLinkedBoss(e);
      return;
    }
    if (!this.enemies.delete(e.id)) return;
    if (killer && World.isLiving(killer)) killer.gold += e.goldReward;
    for (const p of this.players.values()) {
      if (!World.isLiving(p)) continue;
      p.xp += e.xpReward;
    }
    // feature 3.4 (criteria B / 3–5) — roll a trash-mob item drop at the dead
    // enemy's position. `creditKill` runs exactly once per kill (the enemy is
    // deleted just above), so this is one rng draw for a failed roll and two for
    // a success — deterministic given the kill order, off the seeded per-wave
    // `this.rng` (same source as wave spawns).
    // feature 5.2 (§C12) — a boss death clears the wave: remove every remaining
    // add so the end-of-tick `enemies.size === 0` check rolls into `wavebreak`
    // (the boss IS the wave). `winRun` (wave-30 seam, feature 5.4) instead sets
    // `runWon` and routes that same check to the terminal `victory` phase. No
    // trash-mob drop roll for a boss — bosses get their own loot rules later
    // (the `drops` block is explicitly non-boss).
    if (e.boss) {
      const b = DATA.bosses[e.bossId ?? ''];
      if (b?.onDeath === 'winRun') this.winRun();
      else for (const other of [...this.enemies.values()]) this.enemies.delete(other.id);
      return;
    }
    const drops = DATA.drops;
    if (this.rng() < drops.chance) {
      const itemId = pickWeighted(this.rng, drops.weights);
      if (itemId) this.spawnGroundItem(itemId, e.x, e.y);
    }
    // feature 5.1a (criterion 11) — a kamikaze that died from damage detonates
    // now (a "death explosion"). Kill credit was already paid above; the blast
    // is pure hero damage + fx, no further credit. Contact detonation (imp still
    // alive) runs from `tickKamikaze` instead and never reaches `creditKill`.
    if (e.ai === 'kamikaze') this.impExplosion(e);
  }

  /**
   * feature 5.3b (§B6) — put a linked boss into the downed state: pin it at 0 hp,
   * stop it acting, and start its revive timer. Idempotent (a downed twin that
   * keeps taking damage stays downed, hp free to go negative — § the gate is
   * "all downed at once", not "hp held at 0").
   */
  private downLinkedBoss(e: SimEnemy): void {
    if (e.downed) return;
    const b = DATA.bosses[e.bossId ?? ''];
    e.downed = true;
    e.hp = 0;
    e.reviveTimerMs = b?.reviveWindowMs ?? 0;
    e.castAbilityId = null;
    e.castRemainingMs = 0;
    e.castTotalMs = 0;
    e.globalCastGapMs = 0;
    e.vx = 0;
    e.vy = 0;
    e.moving = false;
    e.path = null;
    e.pathIndex = 0;
    this.fx.push({ kind: 'enemyExplode', x: e.x, y: e.y, radius: e.radius * 2 });
  }

  /**
   * feature 5.3b (§B7 / §B8) — one per-tick pass over the linked boss groups:
   *  1. decrement every downed twin's `reviveTimerMs`;
   *  2. for each group (Map insertion order) — if EVERY member is downed, the
   *     group dies at once (`killLinkedGroup`: shared XP once per twin, gold to
   *     each twin's last hitter, then `onDeath` — `clearWave` removes the
   *     remaining adds so the end-of-tick empty check rolls into `wavebreak`);
   *  3. otherwise, any downed twin whose timer has hit 0 revives at
   *     `maxHp * reviveHpFrac` while a partner is still up.
   * Deterministic: `this.enemies` / group iteration is Map order, no `this.rng`.
   */
  private tickLinkedBosses(dtMs: number): void {
    let anyLinked = false;
    for (const e of this.enemies.values()) {
      if (!e.boss || !e.linkGroupId) continue;
      anyLinked = true;
      if (e.downed) e.reviveTimerMs = Math.max(0, (e.reviveTimerMs ?? 0) - dtMs);
    }
    if (!anyLinked) return;

    const groups = new Map<string, SimEnemy[]>();
    for (const e of this.enemies.values()) {
      if (!e.boss || !e.linkGroupId) continue;
      let members = groups.get(e.linkGroupId);
      if (!members) {
        members = [];
        groups.set(e.linkGroupId, members);
      }
      members.push(e);
    }

    for (const members of groups.values()) {
      if (members.every((m) => m.downed)) {
        this.killLinkedGroup(members);
        continue;
      }
      for (const m of members) {
        if (!m.downed || (m.reviveTimerMs ?? 0) > 0) continue;
        if (!members.some((o) => o !== m && !o.downed)) continue;
        const b = DATA.bosses[m.bossId ?? ''];
        m.downed = false;
        m.reviveTimerMs = 0;
        m.hp = Math.max(1, m.maxHp * (b?.reviveHpFrac ?? 0.35));
        m.globalCastGapMs = b?.phases[m.phaseIndex ?? 0]?.castGapMs ?? 0;
        this.fx.push({ kind: 'enemyHeal', x: m.x, y: m.y });
      }
    }
  }

  /**
   * feature 5.3b (§B8) — the whole linked group is downed: pay each twin's
   * rewards (`creditKill`-equivalent — shared `xpReward` to every living player
   * once per twin, `goldReward` to that twin's last hitter if still living),
   * remove them, then apply the boss def's `onDeath` (`clearWave` removes the
   * remaining adds; `winRun` is the wave-30 seam).
   */
  private killLinkedGroup(members: SimEnemy[]): void {
    for (const m of members) {
      const hitter = m.lastHitterId ? this.players.get(m.lastHitterId) : undefined;
      if (hitter && World.isLiving(hitter)) hitter.gold += m.goldReward;
      for (const p of this.players.values()) {
        if (!World.isLiving(p)) continue;
        p.xp += m.xpReward;
      }
      this.enemies.delete(m.id);
    }
    const b = DATA.bosses[members[0]!.bossId ?? ''];
    if (b?.onDeath === 'winRun') this.winRun();
    else for (const other of [...this.enemies.values()]) this.enemies.delete(other.id);
  }

  /** feature 3.4 — put a dropped item on the floor at `(x, y)`. */
  private spawnGroundItem(itemId: string, x: number, y: number): void {
    const id = this.nextGroundItemId++;
    this.groundItems.set(id, { id, itemId, x, y });
  }

  /**
   * feature 5.3a (§A2) — spawn a floor-hazard zone. `magnitude` is damage per
   * `HAZARD_TICK_MS` (`kind: 'damage'`) or the slow fraction (`kind: 'slow'`);
   * `sourceId` is the originating enemy id as a string (used as the `slow`
   * status source). Deterministic — no RNG, id from a monotonic counter.
   */
  spawnHazard(
    x: number,
    y: number,
    radius: number,
    kind: HazardKind,
    magnitude: number,
    durationMs: number,
    sourceId: string,
    shrinkPerMs?: number,
    minRadius?: number,
  ): void {
    const id = this.nextHazardId++;
    const h: SimHazard = {
      id,
      x,
      y,
      radius,
      kind,
      magnitude,
      remainingMs: durationMs,
      tickAccumMs: 0,
      sourceId,
    };
    // feature 5.3c — only carry the shrink fields when set, so a static hazard's
    // snapshot JSON stays byte-identical to 5.3a/b.
    if (shrinkPerMs !== undefined) h.shrinkPerMs = shrinkPerMs;
    if (minRadius !== undefined) h.minRadius = minRadius;
    this.hazards.set(id, h);
  }

  /**
   * feature 5.3a (§A3) — age every hazard by `dtMs` (deleted at `remainingMs <=
   * 0`) and apply it to every living hero standing in it. `damage` hazards fire
   * once per whole `HAZARD_TICK_MS` crossed (per-hazard accumulator, like
   * `tickPeriodicStatuses`); `slow` hazards re-apply a short slow every tick so
   * it lapses just after the hero steps out. Deterministic: hazards in Map
   * insertion order, heroes in Map order, no `this.rng` (§A7).
   */
  private tickHazards(dtMs: number): void {
    if (this.hazards.size === 0) return;
    for (const h of [...this.hazards.values()]) {
      h.remainingMs -= dtMs;
      if (h.remainingMs <= 0) {
        this.hazards.delete(h.id);
        continue;
      }
      // feature 5.3c (§A3) — a shrinking zone closes toward `minRadius`. `dtMs`-
      // scaled + deterministic; static hazards are untouched.
      if (h.shrinkPerMs && h.shrinkPerMs > 0) {
        h.radius = Math.max(h.minRadius ?? 0, h.radius - h.shrinkPerMs * dtMs);
      }
      // feature 5.3c (§A2) — a `safe` zone has no direct effect (it only
      // suppresses `burnOutside` / `damage` / `arenaDamage` — checked at those
      // sites via `heroInAnySafeHazard`).
      if (h.kind === 'safe') continue;
      if (h.kind === 'damage' || h.kind === 'burnOutside') {
        h.tickAccumMs += dtMs;
        while (h.tickAccumMs >= HAZARD_TICK_MS) {
          h.tickAccumMs -= HAZARD_TICK_MS;
          for (const p of this.players.values()) {
            if (!World.isLiving(p)) continue;
            const inside = Math.hypot(p.x - h.x, p.y - h.y) <= h.radius;
            // `damage` hits INSIDE `radius`; `burnOutside` (§A1) hits OUTSIDE it.
            if (h.kind === 'damage' ? !inside : inside) continue;
            // §A1/§A2 — a hero on a `safe` vent takes no hazard tick.
            if (this.heroInAnySafeHazard(p)) continue;
            this.dealEnemyDamage(p, h.magnitude);
            this.fx.push({ kind: 'hit', x: p.x, y: p.y });
          }
        }
      } else {
        for (const p of this.players.values()) {
          if (!World.isLiving(p)) continue;
          if (Math.hypot(p.x - h.x, p.y - h.y) > h.radius) continue;
          applyStatus(p, 'slow', HAZARD_TICK_MS * 2, h.magnitude, h.sourceId);
        }
      }
    }
  }

  /**
   * feature 3.4 (criterion 8) — ground-item pickup, run every `match` AND
   * `wavebreak` tick (criterion 9 — drops persist through the break so players
   * can collect them while shopping). For each ground item in Map insertion
   * order, the **lowest-index living player** (has a class, `!dead`, `hp > 0`)
   * in Map insertion order who is within `DATA.drops.pickupRadius` **and has a
   * free inventory slot** picks it up: the item id goes into their lowest free
   * slot, `recomputeStats` re-derives their stats, and the ground item is
   * removed. An item with no eligible player in range stays on the floor
   * (criterion 8) and never grants stats until picked up (criterion 10).
   *
   * Deterministic: no RNG; ground items and players are both iterated in Map
   * insertion order. Unlike `buyItem` this does NOT refill hp/mana — a pickup
   * can happen mid-wave, so a free heal would be exploitable.
   */
  private tickGroundItemPickups(): void {
    if (this.groundItems.size === 0) return;
    const radius = DATA.drops.pickupRadius;
    for (const gi of [...this.groundItems.values()]) {
      for (const p of this.players.values()) {
        if (!World.isLiving(p)) continue;
        const slot = p.inventory.findIndex((s) => s === null);
        if (slot < 0) continue;
        if (Math.hypot(p.x - gi.x, p.y - gi.y) > radius) continue;
        p.inventory[slot] = gi.itemId;
        this.recomputeStats(p);
        this.groundItems.delete(gi.id);
        break;
      }
    }
  }

  /**
   * feature 3.4 (criterion 9) — wave-clear loot vacuum, run once from
   * `enterWaveBreak` after the revive / heal loop. Players are frozen at spawn
   * for the whole break (`tickWaveBreak` runs no locomotion), so they cannot
   * walk to a drop that landed away from their spawn — instead every ground
   * item still on the floor is granted now to the **lowest-index living player**
   * (`World.isLiving`, Map insertion order) who has a **free inventory slot**,
   * with **no distance / `pickupRadius` check**. An item nobody has room for
   * stays on the floor (and persists into the next wave). `recomputeStats`
   * re-derives the stat block; there is **no hp/mana refill** — a wave-end loot
   * grant is not a heal. No RNG; deterministic given the ground-item and player
   * Map orders.
   */
  private vacuumGroundItems(): void {
    if (this.groundItems.size === 0) return;
    for (const gi of [...this.groundItems.values()]) {
      for (const p of this.players.values()) {
        if (!World.isLiving(p)) continue;
        const slot = p.inventory.findIndex((s) => s === null);
        if (slot < 0) continue;
        p.inventory[slot] = gi.itemId;
        this.recomputeStats(p);
        this.groundItems.delete(gi.id);
        break;
      }
    }
  }

  // ---------------------------------------------------------------- abilities

  /** Resolve every queued cast in arrival order (criterion 15). */
  private consumeCasts(): void {
    if (this.castQueue.length === 0) return;
    const queued = this.castQueue;
    this.castQueue = [];
    for (const c of queued) this.resolveCast(c);
  }

  /**
   * Validate and apply one cast. Silently rejected (no mana / cooldown change)
   * unless: phase is `match`; caster exists, has a class, is alive; the ability
   * is off cooldown; mana covers the cost (criterion 16).
   */
  private resolveCast(c: QueuedCast): void {
    if (this.phase !== 'match') return;
    const p = this.players.get(c.id);
    if (!p || !p.cls || p.dead) return;

    // Stun blocks casting entirely (criterion 4) — silent reject, no cost.
    if (hasStatus(p, 'stun')) return;

    // Q / W / E are silently ignored while a Whirlwind channel is active
    // (criterion 16); only R (the ultimate slot itself) is exempt.
    if (c.slot !== 'r' && hasStatus(p, 'whirlwind')) return;

    // feature 4.2 (criterion 7) / feature 4.5 (criterion 6) — an un-activated
    // ability cannot be cast. `abilityActivated` counts a spent point OR a held
    // `+1 <slot>` item; with no such item it is exactly `abilityPoints[slot] >= 1`.
    // Silent reject before mana / cooldown, like the gates above. The auto basic
    // attack is a separate path and stays unaffected (criterion 8).
    if (!abilityActivated(p, c.slot)) return;

    const slotIdx = ABILITY_SLOTS.indexOf(c.slot);
    if (slotIdx < 0) return;
    // feature 4.3 (criterion 9) — every cast handler reads the ability's numbers
    // through `effectiveAbility` (owned talent nodes applied), not the raw DATA
    // def. With no owned nodes this returns the raw def unchanged, so the cast
    // is byte-identical to pre-4.3 (criterion 11). `manaCost` / `cooldownMs`
    // below are the talent-modified values (e.g. Alacrity −12% cd, Disengage
    // Frugality −25% mana).
    const ability = effectiveAbility(p, abilityBook(p.cls)[c.slot]);
    if (p.abilityCd[slotIdx]! > 0) return;
    if (p.mana < ability.manaCost) return;

    // Valid cast: pay costs, face the cursor (every non-self ability — both a
    // `point` impact and a `direction` aim point toward the cursor), apply the
    // effect.
    p.mana -= ability.manaCost;
    p.abilityCd[slotIdx] = ability.cooldownMs;
    if (ability.targeting !== 'self') {
      p.facing = Math.atan2(c.y - p.y, c.x - p.x);
    }

    // feature 1.5 (criterion B) — a windup ability charges cost + cooldown now
    // and schedules its AoE effect body to resolve at the point locked here.
    if (ability.windupMs && ability.windupMs > 0) {
      this.scheduleWindup(p, ability, c.x, c.y);
      return;
    }

    switch (ability.kind) {
      case 'nuke':
        this.castNuke(p, ability, c.x, c.y);
        break;
      case 'blink':
        this.castBlink(p, ability, c.x, c.y);
        break;
      case 'aoe':
        this.castAoe(p, ability, c.x, c.y);
        break;
      case 'healAoe':
        this.castHealAoe(p, ability);
        break;
      case 'partyBuff':
        this.castPartyBuff(p, ability);
        break;
      case 'charge':
        this.castCharge(p, ability, c.x, c.y);
        break;
      case 'meleeArc':
        this.castCleave(p, ability, c.x, c.y);
        break;
      case 'projectile':
        this.castProjectile(p, ability, c.x, c.y);
        break;
      case 'selfGuard':
        this.castSelfGuard(p, ability);
        break;
      case 'whirlwind':
        this.castWhirlwind(p, ability);
        break;
    }
  }

  /**
   * Ability damage: `(base + primaryAttrValue * scale) * (1 + damageBonus)`
   * (feature 1.3a criterion 14; feature 1.4 `damageBonus`). For the Warrior the
   * primary attribute IS strength, so `p.primaryAttrValue === p.strength` and,
   * absent a `damageBonus` status, every Warrior number is unchanged.
   */
  private static abilityDamage(p: SimPlayer, base: number, scale: number): number {
    return (base + p.primaryAttrValue * scale) * (1 + statusMagnitude(p, 'damageBonus'));
  }

  /**
   * feature 1.3a / 1.4 — apply every entry of an ability's `onHitStatuses` list
   * to `target` (an enemy), sourced to the caster.
   */
  private applyOnHitStatuses(
    sourceId: string | undefined,
    target: SimEnemy,
    list: AbilityDef['onHitStatuses'],
  ): void {
    if (!list) return;
    for (const s of list) {
      applyStatus(target, s.kind, s.durationMs, s.magnitude, sourceId, s.tickIntervalMs);
    }
  }

  /**
   * Apply ability damage to an enemy through the same armor mitigation the basic
   * attack uses (`max(1, dmg - enemyArmor)` — criterion 12), spawn a hit FX,
   * apply every `onHitStatuses` entry to the enemy (feature 1.3a / 1.4), and
   * credit the kill to the caster.
   */
  private dealAbilityDamage(
    p: SimPlayer,
    e: SimEnemy,
    rawDamage: number,
    onHitStatuses?: AbilityDef['onHitStatuses'],
  ): number {
    // feature 5.2 (§C11) — a boss ignores ALL incoming damage (and on-hit
    // statuses / kill credit) during its post-phase-transition invuln window.
    if (this.bossDamageBlocked(e)) {
      this.fx.push({ kind: 'hit', x: e.x, y: e.y });
      return 0;
    }
    const dealt = Math.max(1, rawDamage - e.armor);
    e.hp -= dealt;
    this.fx.push({ kind: 'hit', x: e.x, y: e.y });
    this.applyOnHitStatuses(p.id, e, onHitStatuses);
    this.creditKill(p, e);
    // feature 1.3c — the mitigated amount actually taken off HP, so a caster with
    // `lifestealFrac` (Drain Life) can heal for a fraction of it.
    return dealt;
  }

  /**
   * feature 1.3b — heal a player, capped at `maxHp`, skipped when dead. The
   * single place healing is applied (Circle of Healing, Regrowth's `regen`).
   */
  private dealHeal(p: SimPlayer, amount: number): void {
    // Never heal a dead — or 0-HP not-yet-swept — player (matches applyRegen).
    if (p.dead || p.hp <= 0) return;
    p.hp = Math.min(p.maxHp, p.hp + amount);
  }

  /**
   * `nuke` — damage the single enemy nearest the target point if it is within
   * `castRange` of the caster. Damage = `damage + primaryAttrValue * scale`
   * (feature 1.3a, criterion 7 — Wizard Fireball etc.).
   *
   * feature 1.3c — a `damage: 0` nuke with no effective scaled damage (Warlock
   * Corruption) skips the damage entirely but still applies its `onHitStatuses`
   * to the target (mirrors the `castAoe` 0-damage path). A nuke with
   * `lifestealFrac` (Warlock Drain Life) heals the caster for that fraction of
   * the damage dealt.
   */
  private castNuke(p: SimPlayer, a: AbilityDef, x: number, y: number): void {
    let target: SimEnemy | null = null;
    let best = Infinity;
    for (const e of this.enemies.values()) {
      const d = (e.x - x) ** 2 + (e.y - y) ** 2;
      if (d < best) {
        best = d;
        target = e;
      }
    }
    // Anchor the FX to the resolved enemy (Corruption mark / Drain Life tether),
    // falling back to the cast point when no enemy was found.
    this.fx.push({
      kind: a.fxKind ?? 'abilityBolt',
      x: p.x,
      y: p.y,
      tx: target?.x ?? x,
      ty: target?.y ?? y,
    });
    if (!target) return;
    if (Math.hypot(target.x - p.x, target.y - p.y) > (a.castRange ?? 0)) return;

    const raw = World.abilityDamage(p, a.damage ?? 0, a.scale ?? 0);

    if (raw > 0) {
      const dealt = this.dealAbilityDamage(p, target, raw, a.onHitStatuses);
      if (a.lifestealFrac) this.dealHeal(p, dealt * a.lifestealFrac);
    } else {
      this.applyOnHitStatuses(p.id, target, a.onHitStatuses);
    }
  }

  /**
   * `blink` — the caster hops a fixed `maxRange` directly AWAY from the target
   * point (Ranger Disengage — the only blink today), resolved against arena
   * collision (feature 1.3a, criterion 4).
   */
  private castBlink(p: SimPlayer, a: AbilityDef, x: number, y: number): void {
    const fromX = p.x;
    const fromY = p.y;
    const dx = x - p.x;
    const dy = y - p.y;
    const dist = Math.hypot(dx, dy);
    const max = a.maxRange ?? 0;
    let nx = -dx;
    let ny = -dy;
    if (dist < 1e-6) {
      nx = Math.cos(p.facing);
      ny = Math.sin(p.facing);
    } else {
      nx /= dist;
      ny /= dist;
    }
    const tx = p.x + nx * max;
    const ty = p.y + ny * max;
    const circle = { x: tx, y: ty, r: p.radius };
    resolveAgainstArena(circle);
    p.x = circle.x;
    p.y = circle.y;
    p.vx = 0;
    p.vy = 0;
    p.path = null;
    p.pathIndex = 0;
    this.fx.push({ kind: 'abilityBlink', x: fromX, y: fromY, tx: p.x, ty: p.y });
  }

  /**
   * `aoe` — flat/scaled damage to every enemy within `radius`, each also taking
   * every `onHitStatuses` entry (feature 1.3a / 1.4). `atPoint` centres the
   * blast on the target point (Druid Entangling Roots / Storm, Wizard Meteor);
   * default is caster-centred (Wizard Frost Nova, Warlock Soul Harvest). A
   * 0-damage blast (Entangling Roots) still applies its statuses. When
   * `lifestealFrac` is set (Soul Harvest) the caster heals for that fraction of
   * the total damage dealt across every enemy hit.
   *
   * Determinism: enemies iterated in Map insertion order; the lifesteal total
   * accumulates in that order.
   */
  private castAoe(p: SimPlayer, a: AbilityDef, x: number, y: number): void {
    this.resolveAoeEffect(p, p.id, a, x, y);
  }

  /**
   * The `aoe` effect body, callable both directly (`castAoe`) and from a
   * resolved windup pending effect (feature 1.5) — so the resolution never
   * re-charges mana / cooldown.
   *
   * `caster` may be `undefined` when a windup ability's caster disconnected
   * during the delay (criterion 8): the blast still lands at the locked point,
   * but with no primary-attribute scaling (base `damage` only), no kill credit
   * and no lifesteal. A merely-dead caster is still in the player map and
   * resolves at full strength.
   */
  private resolveAoeEffect(
    caster: SimPlayer | undefined,
    casterId: string,
    a: AbilityDef,
    x: number,
    y: number,
  ): void {
    const radius = a.radius ?? 0;
    const cx = a.atPoint ? x : (caster?.x ?? x);
    const cy = a.atPoint ? y : (caster?.y ?? y);
    const dmg = caster ? World.abilityDamage(caster, a.damage ?? 0, a.scale ?? 0) : (a.damage ?? 0);
    this.fx.push({ kind: a.fxKind ?? 'abilityNova', x: cx, y: cy, radius });
    let totalDealt = 0;
    for (const e of [...this.enemies.values()]) {
      if (Math.hypot(e.x - cx, e.y - cy) > radius) continue;
      if (dmg > 0) {
        if (caster) {
          totalDealt += this.dealAbilityDamage(caster, e, dmg, a.onHitStatuses); // armor-mitigated
        } else if (!this.bossDamageBlocked(e)) {
          e.hp -= Math.max(1, dmg - e.armor);
          this.fx.push({ kind: 'hit', x: e.x, y: e.y });
          this.applyOnHitStatuses(undefined, e, a.onHitStatuses);
          // feature 2.1 — a windup kill with no caster still shares XP to the living.
          this.creditKill(undefined, e);
        }
      } else {
        this.applyOnHitStatuses(caster ? casterId : undefined, e, a.onHitStatuses);
      }
    }
    if (caster && a.lifestealFrac && totalDealt > 0) {
      this.dealHeal(caster, totalDealt * a.lifestealFrac);
    }
  }

  // ------------------------------------------------------ feature 1.5 windups

  /**
   * feature 1.5 (criterion B) — schedule a windup ability: push a pending
   * effect resolving `windupMs` later (rounded up to a whole tick) at the locked
   * point, and spawn a synced danger-zone telegraph for the client to render.
   */
  private scheduleWindup(p: SimPlayer, a: AbilityDef, x: number, y: number): void {
    const telegraphId = this.nextTelegraphId++;
    const resolveTick = this.serverTick + Math.ceil((a.windupMs ?? 0) / TICK_MS);
    this.pendingEffects.push({
      source: 'player',
      resolveTick,
      casterId: p.id,
      abilityId: a.id,
      x,
      y,
      telegraphId,
    });
    this.telegraphs.set(telegraphId, {
      id: telegraphId,
      x,
      y,
      radius: a.radius ?? 0,
      remainingMs: a.windupMs ?? 0,
      totalMs: a.windupMs ?? 0,
      kind: a.fxKind ?? a.id,
      hostile: false,
    });
  }

  /**
   * feature 1.5 — resolve every pending effect whose `resolveTick` has been
   * reached, in `(resolveTick, insertion)` order, by running the AoE effect
   * body at its locked point; drop each resolved telegraph. Runs at a fixed
   * step in `tickMatch` before movement / combat.
   */
  private resolvePendingEffects(): void {
    if (this.pendingEffects.length === 0) return;
    const ready = this.pendingEffects
      .map((pe, i) => ({ pe, i }))
      .filter((r) => r.pe.resolveTick <= this.serverTick)
      .sort((a, b) => a.pe.resolveTick - b.pe.resolveTick || a.i - b.i);
    for (const { pe } of ready) {
      if (pe.source === 'enemy') {
        // feature 5.1b / 5.2 / 5.3a — hostile ground-AoE / boss dash / hazard
        // spawn; all resolve even if the source enemy died during the windup.
        if (pe.kind === 'dash') this.resolveBossDash(pe);
        else if (pe.kind === 'hazard') this.resolveHazardSpawn(pe);
        else this.resolveEnemyAoe(pe);
        this.telegraphs.delete(pe.telegraphId);
        continue;
      }
      const raw = ABILITY_BY_ID[pe.abilityId];
      // feature 4.3 (criterion 9) — re-apply the caster's owned talent nodes at
      // resolution (the pending record only stores the ability id). A
      // disconnected caster resolves off the raw def (base numbers only —
      // matches the feature 1.5 rule for attribute scaling).
      const caster = this.players.get(pe.casterId);
      const a = raw && caster ? effectiveAbility(caster, raw) : raw;
      // Every `windupMs` ability today is a ground `aoe` (Meteor / Entangling
      // Storm / Entangling Roots), so the resolution is always the AoE effect
      // body. A future non-`aoe` windup ability must add its own branch here.
      if (a && a.kind === 'aoe') {
        this.resolveAoeEffect(caster, pe.casterId, a, pe.x, pe.y);
      } else if (a) {
        console.warn(`resolvePendingEffects: no resolver for windup kind '${a.kind}' (${a.id})`);
      }
      this.telegraphs.delete(pe.telegraphId);
    }
    this.pendingEffects = this.pendingEffects.filter((pe) => pe.resolveTick > this.serverTick);
  }

  /** feature 1.5 — age every still-active telegraph by one tick. */
  private tickTelegraphs(dtMs: number): void {
    for (const t of this.telegraphs.values()) {
      t.remainingMs = Math.max(0, t.remainingMs - dtMs);
    }
  }

  /** feature 1.5 / 5.3a — drop every scheduled / in-flight effect (wave end /
   * reset): pending windups, telegraphs, projectiles, and floor hazards. Called
   * from `enterWaveBreak` / `beginMatch` / `restartToSelect`. */
  private clearScheduledEffects(): void {
    this.pendingEffects = [];
    this.telegraphs.clear();
    this.projectiles.clear();
    this.hazards.clear();
  }

  // -------------------------------------------------- feature 1.5 projectiles

  /**
   * feature 1.5 (criterion C) — `projectile` — spawn one skillshot from the
   * caster toward the cursor direction (fallback: `facing`). It travels at
   * `projectileSpeed`, pierces every enemy it touches, and expires after
   * `range` px or on an arena wall.
   */
  private castProjectile(p: SimPlayer, a: AbilityDef, x: number, y: number): void {
    let dx = x - p.x;
    let dy = y - p.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) {
      dx = Math.cos(p.facing);
      dy = Math.sin(p.facing);
    } else {
      dx /= len;
      dy /= len;
    }
    const speed = a.projectileSpeed ?? 0;
    this.projectiles.set(this.nextProjectileId, {
      id: this.nextProjectileId,
      x: p.x,
      y: p.y,
      vx: dx * speed,
      vy: dy * speed,
      owner: 'player',
      casterId: p.id,
      abilityId: a.id,
      remainingRange: a.range ?? 0,
      hitRadius: a.hitRadius ?? 0,
      pierced: new Set<number>(),
    });
    this.nextProjectileId++;
    // Muzzle flash on cast (the projectile mark itself is synced state).
    this.fx.push({
      kind: a.fxKind ?? 'abilityPierce',
      x: p.x,
      y: p.y,
      tx: p.x + dx * 44,
      ty: p.y + dy * 44,
    });
  }

  /**
   * feature 1.5 — advance every projectile by one tick (id order): move by
   * velocity, spend range, hit each not-yet-pierced enemy (Map insertion order)
   * whose body is within `hitRadius + enemyRadius`, expire at `range <= 0` or on
   * a wall. Deterministic — no RNG. A disconnected caster still lands hits
   * (base `damage` only, no kill credit).
   */
  private stepProjectiles(dt: number): void {
    if (this.projectiles.size === 0) return;
    for (const pr of [...this.projectiles.values()]) {
      const step = Math.hypot(pr.vx, pr.vy) * dt;
      pr.x += pr.vx * dt;
      pr.y += pr.vy * dt;
      pr.remainingRange -= step;

      if (pr.owner === 'enemy') {
        // feature 5.1b — hostile projectile: hit the FIRST living hero within
        // `hitRadius + heroRadius` (Map order), `dealEnemyDamage` + `onHitStatus`,
        // NO kill credit, NO pierce — expire on that hit. Otherwise fall through
        // to the shared range / wall expiry below.
        if (this.stepEnemyProjectileHit(pr)) continue;
      } else {
        this.stepPlayerProjectileHits(pr);
      }

      if (pr.remainingRange <= 0) {
        this.projectiles.delete(pr.id);
        continue;
      }
      const probe = { x: pr.x, y: pr.y, r: pr.hitRadius };
      if (resolveAgainstArena(probe)) this.projectiles.delete(pr.id);
    }
  }

  /** feature 1.5 — player skillshot: damage + pierce every not-yet-hit enemy
   * (Map insertion order) whose body overlaps this tick. */
  private stepPlayerProjectileHits(pr: SimProjectile): void {
    const raw = ABILITY_BY_ID[pr.abilityId];
    const caster = this.players.get(pr.casterId);
    // feature 4.3 (criterion 9) — the caster's talent nodes (e.g. Piercing
    // Shot Potency +15% damage) apply to each hit; a disconnected caster
    // resolves off the raw def.
    const a = raw && caster ? effectiveAbility(caster, raw) : raw;
    const dmg = caster
      ? World.abilityDamage(caster, a?.damage ?? 0, a?.scale ?? 0)
      : (a?.damage ?? 0);
    for (const e of [...this.enemies.values()]) {
      if (pr.pierced.has(e.id)) continue;
      if (Math.hypot(e.x - pr.x, e.y - pr.y) > pr.hitRadius + e.radius) continue;
      if (caster) {
        this.dealAbilityDamage(caster, e, dmg, a?.onHitStatuses);
      } else if (!this.bossDamageBlocked(e)) {
        e.hp -= Math.max(1, dmg - e.armor);
        this.fx.push({ kind: 'hit', x: e.x, y: e.y });
        this.applyOnHitStatuses(undefined, e, a?.onHitStatuses);
        // feature 2.1 — a projectile kill with no caster still shares XP to the living.
        this.creditKill(undefined, e);
      }
      pr.pierced.add(e.id);
    }
  }

  /**
   * feature 5.1b — hostile projectile: hit the first living hero (Map insertion
   * order) whose body overlaps this tick. Returns `true` (and removes the
   * projectile — no pierce) when a hit landed. No kill credit.
   */
  private stepEnemyProjectileHit(pr: SimProjectile): boolean {
    for (const p of this.players.values()) {
      if (!World.isLiving(p)) continue;
      if (Math.hypot(p.x - pr.x, p.y - pr.y) > pr.hitRadius + p.radius) continue;
      this.dealEnemyDamage(p, pr.damage ?? 0);
      const s = pr.onHitStatus;
      if (s) applyStatus(p, s.kind, s.durationMs, s.magnitude, pr.casterId, s.tickIntervalMs);
      this.fx.push({ kind: 'hit', x: p.x, y: p.y });
      this.projectiles.delete(pr.id);
      return true;
    }
    return false;
  }

  /**
   * feature 1.3b / 1.4 — `healAoe` (Cleric Circle of Healing, Cleric Sanctuary).
   * Heals every living ally (has a class, `hp > 0`, not dead) within `radius` of
   * the caster, including the caster, by an Energy-scaled flat amount. A heal FX
   * lands on each ally. feature 1.4 — Sanctuary additionally grants each healed
   * ally a `regen` (`regenPerTick` / `tickIntervalMs`) and `damageReduction`
   * status for `durationMs`. Deterministic: allies iterated in Map insertion
   * order.
   */
  private castHealAoe(p: SimPlayer, a: AbilityDef): void {
    const radius = a.radius ?? 0;
    const amount = World.abilityDamage(p, a.heal ?? 0, a.scale ?? 0);
    const dur = a.durationMs ?? 0;
    this.fx.push({ kind: a.fxKind ?? 'abilityCircleHeal', x: p.x, y: p.y, radius });
    for (const ally of this.players.values()) {
      if (ally.dead || !ally.cls || ally.hp <= 0) continue;
      if (Math.hypot(ally.x - p.x, ally.y - p.y) > radius) continue;
      this.dealHeal(ally, amount);
      this.fx.push({ kind: 'abilityHeal', x: ally.x, y: ally.y });
      if (dur > 0 && a.regenPerTick !== undefined) {
        applyStatus(ally, 'regen', dur, a.regenPerTick, p.id, a.tickIntervalMs);
      }
      if (dur > 0 && a.damageReduction !== undefined) {
        applyStatus(ally, 'damageReduction', dur, a.damageReduction, p.id);
      }
    }
  }

  /**
   * feature 1.3b — `partyBuff` (Cleric Blessing). Applies `armorBonus` +
   * `damageReduction` (whichever the def carries) plus a `blessing` HUD marker to
   * every living ally within `radius` of the caster, for `durationMs`. No damage.
   * Deterministic: allies iterated in Map insertion order.
   */
  private castPartyBuff(p: SimPlayer, a: AbilityDef): void {
    const radius = a.radius ?? 0;
    const dur = a.durationMs ?? 0;
    this.fx.push({ kind: a.fxKind ?? 'abilityBlessing', x: p.x, y: p.y, radius });
    for (const ally of this.players.values()) {
      if (ally.dead || !ally.cls || ally.hp <= 0) continue;
      if (Math.hypot(ally.x - p.x, ally.y - p.y) > radius) continue;
      if (a.armorBonus !== undefined) applyStatus(ally, 'armorBonus', dur, a.armorBonus, p.id);
      if (a.damageReduction !== undefined) {
        applyStatus(ally, 'damageReduction', dur, a.damageReduction, p.id);
      }
      applyStatus(ally, 'blessing', dur, 1, p.id);
      this.fx.push({ kind: 'abilityBlessing', x: ally.x, y: ally.y });
    }
  }

  // -------------------------------------------------------------- Warrior kit

  /**
   * Q — Charge (criterion 13). The caster instantly dashes toward the target
   * point, up to `maxRange`, stopping just short of the first enemy whose body
   * intersects a corridor of width `corridorWidth` along the dash line (else at
   * `maxRange`). The landing point is arena-collided. The first enemy hit takes
   * STR-scaled damage, is knocked back `knockbackDistance` px along the dash
   * direction (arena-collided), and is stunned for `stunMs`.
   *
   * Determinism: enemies are scanned in Map insertion order; the earliest
   * intersection wins, ties broken by that order.
   */
  private castCharge(p: SimPlayer, a: AbilityDef, x: number, y: number): void {
    const fromX = p.x;
    const fromY = p.y;
    let dx = x - fromX;
    let dy = y - fromY;
    const aim = Math.hypot(dx, dy);
    if (aim < 1e-6) {
      dx = Math.cos(p.facing);
      dy = Math.sin(p.facing);
    } else {
      dx /= aim;
      dy /= aim;
    }
    const maxRange = a.maxRange ?? 0;
    const travel = Math.min(aim < 1e-6 ? maxRange : aim, maxRange);
    const half = (a.corridorWidth ?? 0) / 2;

    let hit: SimEnemy | null = null;
    let hitDist = Infinity;
    for (const e of this.enemies.values()) {
      const relX = e.x - fromX;
      const relY = e.y - fromY;
      const along = relX * dx + relY * dy; // distance projected onto the dash line
      if (along <= 0 || along - e.radius > travel) continue;
      const perp = Math.abs(relX * -dy + relY * dx); // perpendicular offset
      if (perp > half + e.radius) continue;
      const stopAt = Math.max(0, along - e.radius - p.radius);
      if (stopAt < hitDist) {
        hitDist = stopAt;
        hit = e;
      }
    }

    const landDist = hit ? Math.min(travel, hitDist) : travel;
    const land = { x: fromX + dx * landDist, y: fromY + dy * landDist, r: p.radius };
    resolveAgainstArena(land);
    p.x = land.x;
    p.y = land.y;
    p.vx = 0;
    p.vy = 0;
    p.path = null;
    p.pathIndex = 0;
    this.fx.push({ kind: 'abilityCharge', x: fromX, y: fromY, tx: p.x, ty: p.y });

    if (!hit) return;
    this.dealAbilityDamage(
      p,
      hit,
      World.abilityDamage(p, a.damage ?? 0, a.scale ?? 0),
      a.onHitStatuses,
    );
    if (!this.enemies.has(hit.id)) return; // died to the impact

    // knock the enemy back along the dash direction (`dx`/`dy` are already unit).
    this.knockbackBody(hit, dx, dy, a.knockbackDistance ?? 0);
    applyStatus(hit, 'stun', a.stunMs ?? 0, 1, p.id);
  }

  /**
   * `meleeArc` — instant cone. Damages every enemy within `range` of the caster
   * and within `arcHalfAngleDeg` of the caster→target direction, armor-mitigated,
   * each taking every `onHitStatuses` entry. Used by Warrior Cleave (criterion
   * 14) and Ranger Volley (feature 1.3a, criterion 11).
   */
  private castCleave(p: SimPlayer, a: AbilityDef, x: number, y: number): void {
    const range = a.range ?? 0;
    this.fx.push({ kind: 'abilityCleave', x: p.x, y: p.y, tx: x, ty: y, radius: range });
    let ux = x - p.x;
    let uy = y - p.y;
    const len = Math.hypot(ux, uy);
    if (len < 1e-6) {
      ux = Math.cos(p.facing);
      uy = Math.sin(p.facing);
    } else {
      ux /= len;
      uy /= len;
    }
    const cosHalf = Math.cos(((a.arcHalfAngleDeg ?? 0) * Math.PI) / 180);
    const dmg = World.abilityDamage(p, a.damage ?? 0, a.scale ?? 0);
    for (const e of [...this.enemies.values()]) {
      const rx = e.x - p.x;
      const ry = e.y - p.y;
      const d = Math.hypot(rx, ry);
      if (d - e.radius > range) continue;
      const cos = d > 1e-6 ? (rx * ux + ry * uy) / d : 1;
      if (cos < cosHalf) continue;
      this.dealAbilityDamage(p, e, dmg, a.onHitStatuses);
    }
  }

  /**
   * `selfGuard` — a self-buff cast. Data-driven so one handler covers Warrior
   * Bulwark (criterion 15: `armorBonus` + `damageReduction` + `taunt`), Wizard
   * Arcane Barrier (feature 1.3a: a `shield` absorb pool), Druid Regrowth (a
   * `regen`) and Ranger Rapid Fire (feature 1.4: `attackSpeedBonus` +
   * `damageBonus`). Only the params present in the ability def are applied.
   */
  private castSelfGuard(p: SimPlayer, a: AbilityDef): void {
    const dur = a.durationMs ?? 0;
    if (a.attackSpeedBonus !== undefined || a.damageBonus !== undefined) {
      // feature 1.4 — Ranger Rapid Fire: a timed offensive self-buff, no damage.
      if (a.attackSpeedBonus !== undefined) {
        applyStatus(p, 'attackSpeedBonus', dur, a.attackSpeedBonus, p.id);
      }
      if (a.damageBonus !== undefined) applyStatus(p, 'damageBonus', dur, a.damageBonus, p.id);
      this.fx.push({ kind: a.fxKind ?? 'abilityRapidFire', x: p.x, y: p.y });
    }
    if (a.regenPerTick !== undefined) {
      // feature 1.3b — Druid Regrowth: a periodic self-heal, no upfront heal.
      applyStatus(p, 'regen', dur, a.regenPerTick, p.id, a.tickIntervalMs);
      this.fx.push({ kind: a.fxKind ?? 'abilityRegrowth', x: p.x, y: p.y });
    }
    if (a.shieldAmount !== undefined) {
      applyStatus(p, 'shield', dur, a.shieldAmount, p.id);
      this.fx.push({ kind: a.fxKind ?? 'abilityBarrier', x: p.x, y: p.y });
    }
    if (a.armorBonus !== undefined || a.damageReduction !== undefined) {
      applyStatus(p, 'armorBonus', dur, a.armorBonus ?? 0, p.id);
      applyStatus(p, 'damageReduction', dur, a.damageReduction ?? 0, p.id);
      this.fx.push({ kind: 'abilityBulwark', x: p.x, y: p.y });
    }
    const tr = a.tauntRadius ?? 0;
    if (tr > 0) {
      for (const e of this.enemies.values()) {
        if (Math.hypot(e.x - p.x, e.y - p.y) > tr) continue;
        applyStatus(e, 'taunt', a.tauntDurationMs ?? 0, 1, p.id);
      }
    }
  }

  /**
   * R — Whirlwind (criterion 16, ultimate). Applies the `whirlwind` status for
   * `durationMs` plus a matching `slow` (`selfSlow`) on the caster, then runs the
   * first damage tick immediately. Subsequent ticks fire from `tickWhirlwind`.
   */
  private castWhirlwind(p: SimPlayer, a: AbilityDef): void {
    const dur = a.durationMs ?? 0;
    applyStatus(p, 'whirlwind', dur, 1, p.id);
    applyStatus(p, 'slow', dur, a.selfSlow ?? 0, p.id);
    p.whirlwindTickTimer = 0;
    this.tickWhirlwind(p, 0); // first tick immediately on cast
  }

  /**
   * One Whirlwind step: advance the tick timer by `dtMs`; when it elapses, deal
   * STR-scaled, armor-mitigated `tickDamage` to every enemy within `radius`.
   * Called once per match tick while the `whirlwind` status is active, and once
   * directly from `castWhirlwind` (with `dtMs = 0`) for the on-cast tick.
   */
  private tickWhirlwind(p: SimPlayer, dtMs: number): void {
    // feature 4.3 (criterion 9) — Whirlwind's per-tick damage / radius read
    // through the caster's owned talent nodes (Potency +15% tickDamage,
    // Expansion +20% radius); `effectiveAbility` returns the raw def when none.
    const a = effectiveAbility(p, abilityBook('warrior').r);
    const interval = a.tickIntervalMs && a.tickIntervalMs > 0 ? a.tickIntervalMs : 500;
    p.whirlwindTickTimer -= dtMs;
    if (p.whirlwindTickTimer > 0) return;
    p.whirlwindTickTimer += interval;

    const radius = a.radius ?? 0;
    const dmg = World.abilityDamage(p, a.tickDamage ?? 0, a.scale ?? 0);
    this.fx.push({ kind: 'abilityWhirlwind', x: p.x, y: p.y, radius });
    for (const e of [...this.enemies.values()]) {
      if (Math.hypot(e.x - p.x, e.y - p.y) > radius) continue;
      this.dealAbilityDamage(p, e, dmg);
    }
  }

  // ---------------------------------------------------------------- output

  consumeFx(): CombatFx[] {
    return this.fx;
  }

  /** Snapshot for tests / debugging (not used on the network path). */
  snapshot(): {
    phase: GamePhase;
    wave: number;
    countdown: number;
    runWon: boolean;
    difficulty: DifficultyTier;
    revivalTokens: number | null;
    players: SimPlayer[];
    enemies: SimEnemy[];
    pendingEffects: PendingEffect[];
    projectiles: (Omit<SimProjectile, 'pierced'> & { pierced: number[] })[];
    telegraphs: Telegraph[];
    groundItems: SimGroundItem[];
    hazards: SimHazard[];
  } {
    return {
      phase: this.phase,
      wave: this.wave,
      countdown: this.countdown,
      // feature 5.3c — carried so the determinism tests cover the wave-30 win.
      runWon: this.runWon,
      // feature 5.5 — carried so the determinism tests cover the difficulty tier.
      difficulty: this.difficulty,
      // feature 5.6 — carried so the determinism tests cover the revival-token budget.
      revivalTokens: this.revivalTokens,
      players: [...this.players.values()],
      enemies: [...this.enemies.values()],
      // feature 3.4 — included so the determinism tests cover enemy drops.
      groundItems: [...this.groundItems.values()].map((g) => ({ ...g })),
      // feature 5.3a — included so the determinism tests cover floor hazards.
      hazards: [...this.hazards.values()].map((h) => ({ ...h })),
      // feature 1.5 — included so the determinism tests cover windups + shots.
      pendingEffects: this.pendingEffects.map((pe) => ({ ...pe })),
      projectiles: [...this.projectiles.values()].map((pr) => ({
        ...pr,
        pierced: [...pr.pierced].sort((x, y) => x - y),
      })),
      telegraphs: [...this.telegraphs.values()].map((t) => ({ ...t })),
    };
  }
}

export type { Vec2 };
